import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useROStore } from "../context/ROContext";
import { useAuth } from "../context/AuthContext";
import { useROUnread } from "../context/ROUnreadContext";
import ROActivityDrawer from "../components/ro/ROActivityDrawer";
import "./Dashboard.css";
import {
  exportKpiRangeXlsx,
  exportWipFilteredXlsx,
  normalizeKpiRow,
  normalizeWipRow,
} from "../utils/excelReport";
import { getManagerDashboardOperationalStatus } from "../utils/roDashboardStatus";

/**
 * Flowtrak — Manager Dashboard
 * Refined premium version:
 * - Fixed-height scrollable Current WIP
 * - Tighter right rail
 * - Slightly shorter WIP table height
 * - Tighter Trend Alerts
 * - Shorter Today button label
 * - Compact empty-state Current Snoozes
 * - Current Snoozes now lives under Current WIP to remove vertical gap
 * - Existing functionality preserved
 */

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function latestSharedNote(notes) {
  const raw = safeString(notes).trim();
  if (!raw) return "";

  const blocks = raw
    .split(/\n\s*\n/)
    .map((part) => safeString(part).trim())
    .filter(Boolean);

  if (!blocks.length) return "";

  const latestBlock = blocks[blocks.length - 1];

  const lines = latestBlock
    .split("\n")
    .map((x) => safeString(x).trim())
    .filter(Boolean);

  if (!lines.length) return "";
  if (lines.length === 1) return lines[0];

  return lines.slice(1).join(" ");
}
function managerThreadButtonStyle(hasUnreadNote) {
  return {
    position: "relative",
    border: hasUnreadNote ? "1px solid #fdba74" : "1px solid rgba(15,23,42,0.12)",
    background: hasUnreadNote ? "#fff7ed" : "#ffffff",
    color: hasUnreadNote ? "#9a3412" : "rgba(15,23,42,0.74)",
    borderRadius: 10,
    padding: "6px 8px",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 10.8,
    whiteSpace: "nowrap",
  };
}

function ManagerUnreadDot() {
  return (
    <span
      style={{
        position: "absolute",
        top: -5,
        right: -5,
        width: 10,
        height: 10,
        borderRadius: 999,
        background: "#f97316",
        border: "2px solid #ffffff",
        boxShadow: "0 0 0 1px rgba(249,115,22,0.18)",
      }}
    />
  );
}

function dateKeyInTZ(date, timeZone) {
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return "";

    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone || "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    return fmt.format(d);
  } catch {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
}

function startOfDayISO(dateKey) {
  const d = new Date(`${dateKey}T00:00:00`);
  return d.toISOString();
}

function endOfDayISO(dateKey) {
  const d = new Date(`${dateKey}T23:59:59`);
  return d.toISOString();
}

function isSameDateKeyTZ(iso, dateKey, timeZone) {
  if (!iso || !dateKey) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return dateKeyInTZ(d, timeZone) === dateKey;
}

function formatTimeShort(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}


function formatDateTimeDisplay(iso, timeZone) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString([], {
      timeZone: timeZone || "America/Los_Angeles",
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function prettyAlertType(type) {
  const s = safeString(type).trim().toUpperCase();
  if (s === "PROMISE_RECOVERY") return "Promise recovery";
  if (s === "ASSIGNMENT_DELAY") return "Assignment delay";
  if (s === "PARTS_DELAY") return "Parts delay";
  if (s === "IN_PROGRESS_AGING") return "Stalled in progress";
  return s
    ? s
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "—";
}

function pillClassForOperationalStatus(status) {
  const s = String(status || "").toUpperCase();
  if (s === "WAITING_PARTS" || s === "WAITING_FOR_PARTS" || s === "WAITING_ON_PARTS") {
    return "ft-pill ft-pill--parts";
  }
  if (s === "WAITING_FOR_ASSIGNMENT" || s === "UNASSIGNED") {
    return "ft-pill ft-pill--assign";
  }
  if (s === "IN_PROGRESS") return "ft-pill ft-pill--prog";
  if (s === "ASSIGNED") return "ft-pill ft-pill--open";
  if (s === "COMPLETED") return "ft-pill ft-pill--prog";
  if (s === "VEHICLE_GONE") return "ft-pill ft-pill--other";
  if (s === "OPEN") return "ft-pill ft-pill--open";
  return "ft-pill ft-pill--open";
}

function pillClassForTransport(tt) {
  const s = String(tt || "").toUpperCase();
  if (s === "WAITER") return "ft-pill ft-pill--waiter";
  if (s === "LOANER") return "ft-pill ft-pill--loaner";
  if (s === "SHUTTLE") return "ft-pill ft-pill--shuttle";
  return "ft-pill ft-pill--other";
}

function getOpenedISO(ro) {
  return (
    ro?.openedAt ||
    ro?.createdAt ||
    ro?.timestamps?.createdAt ||
    ro?.inAt ||
    ro?.arrivedAt ||
    ""
  );
}

function getCreatedISO(ro) {
  return (
    ro?.timestamps?.createdAt ||
    ro?.timestamps?.openedAt ||
    ro?.timestamps?.inAt ||
    ro?.timestamps?.arrivedAt ||
    ro?.createdAtISO ||
    ro?.createdAtIso ||
    ro?.createdAtUtc ||
    ro?.createdAt ||
    ro?.openedAtISO ||
    ro?.openedAt ||
    ro?.inAt ||
    ro?.arrivedAt ||
    ""
  );
}

function createdTimeForRO(ro) {
  const t = new Date(getCreatedISO(ro) || 0).getTime();
  return Number.isFinite(t) && t > 0 ? t : 0;
}

function getPromiseISO(ro) {
  return safeString(
    ro?.promiseTimeISO || ro?.promiseTime || ro?.promise_time_iso || ""
  ).trim();
}

function minutesUntil(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  const n = Date.now();
  if (!Number.isFinite(t)) return null;
  return Math.round((t - n) / 60000);
}

function roKeyForNavigate(ro) {
  const tag = safeString(ro?.tagNumber).trim();
  if (tag) return tag;

  const roNum = safeString(ro?.roNumber).trim();
  if (roNum) return roNum;

  const id = safeString(ro?.id).trim();
  return id || "";
}

function roAttentionIdentity(ro) {
  const id = safeString(ro?.id).trim();
  if (id) return `ID:${id}`;

  const roNumber = safeString(ro?.roNumber).trim();
  if (roNumber) return `RO:${roNumber}`;

  const tagNumber = safeString(ro?.tagNumber).trim();
  if (tagNumber) return `TAG:${tagNumber}`;

  return "UNKNOWN";
}

function sameNonBlankValue(a, b) {
  const aa = safeString(a).trim().toUpperCase();
  const bb = safeString(b).trim().toUpperCase();
  return !!aa && !!bb && aa === bb;
}

function snoozeMatchesAlert(entry, { alertKey, category, ro }) {
  if (!entry || !isSuppressed(entry)) return false;

  if (sameNonBlankValue(entry.alertKey, alertKey)) return true;
  if (!sameNonBlankValue(entry.category, category)) return false;

  const roId = safeString(ro?.id).trim();
  const roNumber = safeString(ro?.roNumber).trim();
  const tagNumber = safeString(ro?.tagNumber).trim();
  const navigateKey = roKeyForNavigate(ro);

  return (
    sameNonBlankValue(entry.roKey, roId) ||
    sameNonBlankValue(entry.roKey, roNumber) ||
    sameNonBlankValue(entry.roKey, tagNumber) ||
    sameNonBlankValue(entry.roKey, navigateKey) ||
    sameNonBlankValue(entry.roNumber, roNumber) ||
    sameNonBlankValue(entry.tagNumber, tagNumber)
  );
}

function findSharedSnoozeForAlert(map, descriptor) {
  const entries = Object.values(map || {});
  return entries.find((entry) => snoozeMatchesAlert(entry, descriptor)) || null;
}

function roLabel(ro) {
  const roNumber = safeString(ro?.roNumber).trim();
  const tag = safeString(ro?.tagNumber).trim();

  if (roNumber) return `RO #${roNumber}`;
  if (tag) return `Tag #${tag}`;

  return safeString(ro?.id).trim()
    ? `RO ${safeString(ro?.id).trim().slice(0, 8)}`
    : "RO";
}

function formatDurationFromMinutes(mins, compact = false) {
  const total = Math.abs(Math.round(safeNum(mins)));

  if (compact) {
    if (total >= 24 * 60) return `${Math.floor(total / (24 * 60))}d`;
    if (total >= 60) return `${Math.floor(total / 60)}h`;
    return `${total}m`;
  }

  if (total < 60) return `${total}m`;

  const hours = Math.floor(total / 60);
  const rem = total % 60;

  if (hours < 24) return rem ? `${hours}h ${rem}m` : `${hours}h`;

  const days = Math.floor(hours / 24);
  const rh = hours % 24;

  return rh ? `${days}d ${rh}h` : `${days}d`;
}

function promiseDisplayLabel(iso, timeZone) {
  if (!iso) return "—";

  const mins = minutesUntil(iso);
  if (mins == null) return "—";

  if (mins < 0) {
    return `Overdue ${formatDurationFromMinutes(mins, true)}`;
  }

  if (mins <= 60) {
    return `Due ${formatDurationFromMinutes(mins, true)}`;
  }

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  const nowKey = dateKeyInTZ(new Date(), timeZone);
  const thatKey = dateKeyInTZ(d, timeZone);

  const timeText = (() => {
    try {
      return d.toLocaleTimeString([], {
        timeZone: timeZone || "America/Los_Angeles",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return d.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
    }
  })();

  if (thatKey === nowKey) return `Today ${timeText}`;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowKey = dateKeyInTZ(tomorrow, timeZone);
  if (thatKey === tomorrowKey) return `Tomorrow ${timeText}`;

  try {
    return d.toLocaleString([], {
      timeZone: timeZone || "America/Los_Angeles",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }
}

function promiseSeverityStyle(mins, isSnoozed = false) {
  if (isSnoozed) {
    return {
      border: "1px solid rgba(15,23,42,0.10)",
      background: "rgba(15,23,42,0.05)",
      color: "rgba(15,23,42,0.56)",
    };
  }

  if (mins == null) {
    return {
      border: "1px solid rgba(15,23,42,0.10)",
      background: "#ffffff",
      color: "rgba(15,23,42,0.66)",
    };
  }

  if (mins >= 0) {
    return {
      border: "1px solid rgba(181,71,8,0.14)",
      background: "rgba(255,247,237,0.92)",
      color: "#b54708",
    };
  }

  const overdue = Math.abs(mins);

  if (overdue < 120) {
    return {
      border: "1px solid rgba(181,71,8,0.16)",
      background: "rgba(255,247,237,0.96)",
      color: "#b54708",
    };
  }

  if (overdue < 24 * 60) {
    return {
      border: "1px solid rgba(194,65,12,0.18)",
      background: "rgba(255,237,213,0.94)",
      color: "#c2410c",
    };
  }

  if (overdue < 72 * 60) {
    return {
      border: "1px solid rgba(180,35,24,0.18)",
      background: "rgba(254,243,242,0.96)",
      color: "#b42318",
    };
  }

  return {
    border: "1px solid rgba(127,29,29,0.22)",
    background: "linear-gradient(180deg, rgba(254,226,226,0.96) 0%, rgba(254,242,242,0.96) 100%)",
    color: "#7f1d1d",
  };
}

function minutesSince(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const n = Date.now();
  return Math.max(0, Math.round((n - t) / 60000));
}

function formatAgeFromMinutes(mins) {
  if (mins == null) return "—";
  const m = Math.max(0, mins);

  if (m < 60) return `${m}m`;

  const h = Math.floor(m / 60);
  const rem = m % 60;

  if (h < 24) return rem ? `${h}h ${rem}m` : `${h}h`;

  const d = Math.floor(h / 24);
  const rh = h % 24;

  return rh ? `${d}d ${rh}h` : `${d}d`;
}

function shopAgeMinutesForRO(ro) {
  return minutesSince(getCreatedISO(ro));
}

function shopAgeLabelForRO(ro) {
  return formatAgeFromMinutes(shopAgeMinutesForRO(ro));
}

function shopAgeDaysForRO(ro) {
  const mins = shopAgeMinutesForRO(ro);
  if (mins == null) return "";
  return Number((mins / 1440).toFixed(1));
}

function shopAgeTextStyle(ro) {
  const mins = shopAgeMinutesForRO(ro);

  if (mins == null) {
    return { color: "rgba(15,23,42,0.58)" };
  }

  if (mins >= 3 * 24 * 60) {
    return { color: "#991b1b", fontWeight: 950 };
  }

  if (mins >= 24 * 60) {
    return { color: "#b54708", fontWeight: 950 };
  }

  return { color: "rgba(15,23,42,0.78)", fontWeight: 900 };
}

function ageAnchorISOForRO(ro) {
  const lc = safeString(ro?.lifecycle || ro?.status).toUpperCase();
  const opened = getOpenedISO(ro);

  if (lc === "WAITING_FOR_ASSIGNMENT" || lc === "UNASSIGNED") {
    return ro?.timestamps?.waitingAssignmentAt || ro?.waitingAssignmentAt || opened;
  }

  if (lc === "WAITING_FOR_PARTS" || lc === "WAITING_ON_PARTS") {
    return (
      ro?.timestamps?.waitingPartsAt ||
      ro?.waitingPartsAt ||
      ro?.waiting_parts_at ||
      opened
    );
  }

  if (lc === "IN_PROGRESS") {
    return (
      ro?.timestamps?.workStartAt ||
      ro?.workStartAt ||
      ro?.timestamps?.startedAt ||
      ro?.startedAt ||
      ro?.started_at ||
      opened
    );
  }

  return opened;
}

function agingDangerForRO(ro) {
  const lc = safeString(ro?.lifecycle || ro?.status).toUpperCase();
  const anchor = ageAnchorISOForRO(ro);
  const mins = minutesSince(anchor);

  if (mins == null) return false;

  if (lc === "WAITING_FOR_ASSIGNMENT" || lc === "UNASSIGNED") return mins >= 30;
  if (lc === "WAITING_FOR_PARTS" || lc === "WAITING_ON_PARTS")
    return mins >= 24 * 60;
  if (lc === "IN_PROGRESS") return mins >= 4 * 60;

  return false;
}

function agingLabelForRO(ro) {
  const anchor = ageAnchorISOForRO(ro);
  const mins = minutesSince(anchor);
  return mins == null ? "—" : `Age ${formatAgeFromMinutes(mins)}`;
}

function dashboardShellCardStyle() {
  return {
    padding: 12,
    borderRadius: 16,
  };
}

function denseSectionTitleStyle() {
  return {
    fontSize: "11px",
    fontWeight: 900,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "rgba(15,23,42,0.72)",
    margin: 0,
  };
}

function kpiTone(value, kind) {
  if (kind === "promiseRisk") {
    if (value >= 3) return { label: "Needs attention", tone: "danger" };
    if (value >= 1) return { label: "Watch closely", tone: "warn" };
    return { label: "Good", tone: "good" };
  }

  if (kind === "unassigned") {
    if (value >= 3) return { label: "Needs action", tone: "danger" };
    if (value >= 1) return { label: "Monitor", tone: "warn" };
    return { label: "Good", tone: "good" };
  }

  if (kind === "waitingParts") {
    if (value >= 4) return { label: "Building up", tone: "danger" };
    if (value >= 1) return { label: "Monitor", tone: "warn" };
    return { label: "Good", tone: "good" };
  }

  if (kind === "inProgress") {
    if (value >= 1) return { label: "Work moving", tone: "neutral" };
    return { label: "None active", tone: "neutral" };
  }

  if (kind === "completedToday") {
    if (value >= 1) return { label: "Cars moving", tone: "good" };
    return { label: "No completions yet", tone: "neutral" };
  }

  if (kind === "openWip") {
    if (value >= 12) return { label: "Heavy load", tone: "warn" };
    if (value >= 1) return { label: "Normal", tone: "neutral" };
    return { label: "Clear", tone: "good" };
  }

  return { label: "", tone: "neutral" };
}

function executiveToneStyle(tone) {
  if (tone === "danger") {
    return {
      border: "1px solid rgba(180,35,24,0.12)",
      background:
        "linear-gradient(180deg, rgba(254,243,242,0.82) 0%, rgba(255,255,255,1) 100%)",
      color: "#b42318",
    };
  }

  if (tone === "warn") {
    return {
      border: "1px solid rgba(181,71,8,0.12)",
      background:
        "linear-gradient(180deg, rgba(255,247,237,0.82) 0%, rgba(255,255,255,1) 100%)",
      color: "#b54708",
    };
  }

  if (tone === "good") {
    return {
      border: "1px solid rgba(6,118,71,0.12)",
      background:
        "linear-gradient(180deg, rgba(236,253,243,0.80) 0%, rgba(255,255,255,1) 100%)",
      color: "#067647",
    };
  }

  return {
    border: "1px solid rgba(15,23,42,0.07)",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%)",
    color: "rgba(15,23,42,0.76)",
  };
}

function toneTextStyle(tone) {
  if (tone === "danger") return { color: "#b42318" };
  if (tone === "warn") return { color: "#b54708" };
  if (tone === "good") return { color: "#067647" };
  return { color: "rgba(15,23,42,0.58)" };
}

function isWaiterRO(ro) {
  return safeString(ro?.transportType).toUpperCase() === "WAITER";
}

function lifecycleKey(ro) {
  return safeString(ro?.lifecycle || ro?.status).trim().toUpperCase();
}

function getCompletedISO(ro) {
  return safeString(
    ro?.completedAtISO ||
      ro?.completedAt ||
      ro?.completed_at ||
      ro?.technicianCompletedAtISO ||
      ro?.technician_completed_at_iso ||
      ro?.timestamps?.completedAt ||
      ro?.timestamps?.technicianCompletedAt ||
      ro?.timestamps?.completed_at ||
      ""
  ).trim();
}

function isDashboardCompletedRO(ro, store) {
  if (typeof store?.isCompletedRO === "function") return store.isCompletedRO(ro);

  const lc = lifecycleKey(ro);
  return (
    lc === "COMPLETED" ||
    lc === "COMPLETE" ||
    lc === "DONE" ||
    !!getCompletedISO(ro)
  );
}

function isDashboardArchivedRO(ro, store) {
  if (typeof store?.isArchived === "function") return store.isArchived(ro);

  const lc = lifecycleKey(ro);
  return (
    lc === "ARCHIVED" ||
    lc === "VOID" ||
    lc === "VOIDED" ||
    !!ro?.archivedAt ||
    !!ro?.archivedAtISO ||
    !!ro?.timestamps?.archivedAt
  );
}

function isDashboardVehicleGoneLike(ro) {
  const pickupStatus = safeString(
    ro?.customerPickupStatus || ro?.customer_pickup_status || ""
  )
    .trim()
    .toUpperCase();

  const advisorAction = safeString(
    ro?.advisorActionStatus || ro?.advisor_action_status || ""
  )
    .trim()
    .toUpperCase();

  const status = safeString(ro?.status).trim().toUpperCase();
  const lifecycle = safeString(ro?.lifecycle || ro?.lifeCycle).trim().toUpperCase();

  return (
    pickupStatus === "VEHICLE_GONE" ||
    advisorAction === "VEHICLE_GONE" ||
    status === "VEHICLE_GONE" ||
    lifecycle === "VEHICLE_GONE" ||
    !!ro?.vehicleGone ||
    !!ro?.vehicle_gone ||
    !!ro?.pickedUp ||
    !!ro?.picked_up ||
    !!ro?.pickedUpAt ||
    !!ro?.pickedUpAtISO ||
    !!ro?.vehicleGoneAt ||
    !!ro?.vehicleGoneAtISO
  );
}

function isDashboardCustomerContacted(ro) {
  const pickupStatus = safeString(
    ro?.customerPickupStatus || ro?.customer_pickup_status || ""
  )
    .trim()
    .toUpperCase();

  const advisorAction = safeString(
    ro?.advisorActionStatus || ro?.advisor_action_status || ""
  )
    .trim()
    .toUpperCase();

  return (
    pickupStatus === "CONTACTED_FOR_PICKUP" ||
    advisorAction === "CUSTOMER_CONTACTED" ||
    !!ro?.customerContacted ||
    !!ro?.customer_contacted ||
    !!ro?.customerContactedAt ||
    !!ro?.customer_contacted_at ||
    !!ro?.customerContactedAtISO ||
    !!ro?.customer_contacted_at_iso ||
    !!ro?.timestamps?.customerContactedAt ||
    !!ro?.timestamps?.customer_contacted_at
  );
}

function shouldShowOnDashboardWip(ro, store, todayKey, timeZone) {
  if (!ro) return false;
  if (isDashboardArchivedRO(ro, store)) return false;
  if (isDashboardCompletedRO(ro, store)) return false;

  /*
    Manager Dashboard is a live operational pressure view.

    Vehicle Gone / picked-up ROs may remain visible on WIP for same-day review,
    but they should never count toward active WIP, promise risk, shop health,
    bottlenecks, priority items, or manager export "Open ROs Now".
  */
  if (isDashboardVehicleGoneLike(ro)) return false;

  return true;
}

function isDashboardOpenRO(ro, store) {
  if (typeof store?.isWipRO === "function") return store.isWipRO(ro);
  return !isDashboardCompletedRO(ro, store) && !isDashboardArchivedRO(ro, store);
}

function isDashboardWaitingForParts(ro, store) {
  const contextSaysWaiting =
    typeof store?.isWaitingForParts === "function"
      ? !!store.isWaitingForParts(ro)
      : false;

  const lifecycle = safeString(ro?.lifecycle || ro?.lifeCycle || "")
    .trim()
    .toUpperCase();

  const status = safeString(ro?.status || "")
    .trim()
    .toUpperCase();

  const displayStatus = safeString(
    ro?.displayStatus ||
      ro?.display_status ||
      ro?.boardStatus ||
      ro?.board_status ||
      ro?.statusDisplay ||
      ro?.status_display ||
      ""
  )
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  const partsStatus = safeString(ro?.partsStatus || ro?.parts_status || "")
    .trim()
    .toUpperCase();

  const partsActionStatus = safeString(
    ro?.partsActionStatus || ro?.parts_action_status || ""
  )
    .trim()
    .toUpperCase();

  const partsRequestType = safeString(
    ro?.partsRequestType || ro?.parts_request_type || ""
  )
    .trim()
    .toUpperCase();

  const hasPartsRequest =
    partsRequestType === "NEED_PARTS" ||
    partsRequestType === "NEED_PRICE" ||
    partsRequestType === "PARTS_NEEDED" ||
    partsRequestType === "QUOTE_NEEDED";

  const reviewAction =
    !partsActionStatus ||
    partsActionStatus === "REVIEW" ||
    partsActionStatus === "OPEN" ||
    partsActionStatus === "REOPEN";

  const hasOpenPartsRequest = hasPartsRequest && reviewAction;

  const hasBlockingPartsStatus =
    partsStatus === "ORDERED" ||
    partsStatus === "WAITING" ||
    partsStatus === "WAITING_PARTS" ||
    partsStatus === "WAITING_FOR_PARTS";

  const hasBlockingPartsAction =
    partsActionStatus === "ORDERED" ||
    partsActionStatus === "PARTS_ORDERED" ||
    partsActionStatus === "WAITING_PARTS" ||
    partsActionStatus === "WAITING_FOR_PARTS";

  return (
    contextSaysWaiting ||
    lifecycle === "WAITING_FOR_PARTS" ||
    lifecycle === "WAITING_ON_PARTS" ||
    status === "WAITING_FOR_PARTS" ||
    status === "WAITING_ON_PARTS" ||
    status === "WAITING_PARTS" ||
    displayStatus === "WAITING_PARTS" ||
    displayStatus === "WAITING_FOR_PARTS" ||
    hasOpenPartsRequest ||
    hasBlockingPartsStatus ||
    hasBlockingPartsAction
  );
}

function isDashboardWaitingForAssignment(ro) {
  return getManagerDashboardOperationalStatus(ro).key === "UNASSIGNED";
}

function isDashboardInProgress(ro, store) {
  if (typeof store?.isInProgressRO === "function") return store.isInProgressRO(ro);

  const lc = lifecycleKey(ro);
  return lc === "IN_PROGRESS";
}

const ACK_REASONS = [
  "Waiting on warranty approval",
  "Waiting on insurance approval",
  "Waiting on customer response",
  "Waiting on parts ETA",
  "Waiting on sublet/vendor",
  "Already escalated",
  "Other",
];

const SNOOZE_OPTIONS = [
  { value: "1H", label: "1 hour" },
  { value: "4H", label: "4 hours" },
  { value: "TOMORROW", label: "Until tomorrow" },
  { value: "2D", label: "2 days" },
  { value: "7D", label: "7 days" },
  { value: "CUSTOM", label: "Custom date/time" },
];

const WIP_SORT_OPTIONS = [
  { value: "AGE_DESC", label: "Oldest first" },
  { value: "AGE_ASC", label: "Newest first" },
  { value: "PRIORITY", label: "Priority" },
  { value: "PROMISE", label: "Promise time" },
];

function addHours(hours) {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

function addDays(days) {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

function startOfTomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(8, 0, 0, 0);
  return d.toISOString();
}

function toLocalDateTimeInputValue(date) {
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  } catch {
    return "";
  }
}

function parseLocalDateTimeInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

function isSuppressed(entry) {
  if (!entry?.snoozeUntil) return false;
  const t = new Date(entry.snoozeUntil).getTime();
  if (!Number.isFinite(t)) return false;
  return t > Date.now();
}

function cleanupExpiredSnoozes(map) {
  const next = {};
  Object.entries(map || {}).forEach(([key, entry]) => {
    if (isSuppressed(entry)) {
      next[key] = entry;
    }
  });
  return next;
}

function normalizeSharedSnooze(row) {
  const r = row && typeof row === "object" ? row : {};

  return {
    alertKey: safeString(r.alertKey || r.alert_key).trim(),
    category: safeString(r.category).trim(),
    roKey: safeString(r.roKey || r.ro_key).trim(),
    roNumber: safeString(r.roNumber || r.ro_number).trim(),
    tagNumber: safeString(r.tagNumber || r.tag_number).trim(),
    customerName: safeString(r.customerName || r.customer_name).trim(),
    vehicle: safeString(r.vehicle).trim(),
    reason: safeString(r.reason).trim(),
    snoozeMode: safeString(r.snoozeMode || r.snooze_mode).trim(),
    snoozeUntil: safeString(r.snoozeUntil || r.snooze_until).trim(),
    dismissedAt: safeString(r.dismissedAt || r.dismissed_at).trim(),
    actedByUserId: safeString(r.actedByUserId || r.acted_by_user_id).trim(),
    actedByName: safeString(r.actedByName || r.acted_by_name).trim(),
    actedByRole: safeString(r.actedByRole || r.acted_by_role).trim(),
    updatedAt: safeString(r.updatedAt || r.updated_at).trim(),
  };
}

function sharedSnoozeMap(rows) {
  const next = {};

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const item = normalizeSharedSnooze(row);
    if (!item.alertKey || !isSuppressed(item)) return;
    next[item.alertKey] = item;
  });

  return next;
}

function getDefaultSnoozeMode(reason, category) {
  const r = safeString(reason).trim().toUpperCase();
  const c = safeString(category).trim().toUpperCase();

  if (r === "WAITING ON WARRANTY APPROVAL") return "TOMORROW";
  if (r === "WAITING ON INSURANCE APPROVAL") return "TOMORROW";
  if (r === "WAITING ON CUSTOMER RESPONSE") return "TOMORROW";
  if (r === "WAITING ON PARTS ETA") return "4H";
  if (r === "WAITING ON SUBLET/VENDOR") return "2D";
  if (r === "ALREADY ESCALATED") return "TOMORROW";

  if (c === "PROMISE_RECOVERY") return "1H";
  if (c === "ASSIGNMENT_DELAY") return "1H";
  if (c === "IN_PROGRESS_AGING") return "1H";
  if (c === "PARTS_DELAY") return "4H";

  return "4H";
}

function resolveSnoozeUntil(snoozeMode, customIso) {
  if (snoozeMode === "1H") return addHours(1);
  if (snoozeMode === "4H") return addHours(4);
  if (snoozeMode === "TOMORROW") return startOfTomorrowISO();
  if (snoozeMode === "2D") return addDays(2);
  if (snoozeMode === "7D") return addDays(7);
  if (snoozeMode === "CUSTOM") return customIso || "";
  return addHours(4);
}

function describeSnoozeMode(mode, customIso) {
  if (mode === "1H") return "1 hour";
  if (mode === "4H") return "4 hours";
  if (mode === "TOMORROW") return "until tomorrow";
  if (mode === "2D") return "2 days";
  if (mode === "7D") return "7 days";
  if (mode === "CUSTOM") {
    if (!customIso) return "custom time";
    try {
      return new Date(customIso).toLocaleString();
    } catch {
      return "custom time";
    }
  }
  return "4 hours";
}

export default function Dashboard() {
  const store = useROStore();
  const auth = useAuth();
  const {
    hasUnread,
    isHydrated: unreadHydrated,
    storeId: unreadStoreId,
  } = useROUnread();
  const nav = useNavigate();

  const apiGet = auth?.apiGet;
  const apiPost = auth?.apiPost;
  const apiDelete = auth?.apiDelete;

  const currentUser = store?.currentUser || {};
  const actorRole = safeString(currentUser?.role || "manager");
  const actorName = safeString(currentUser?.name || "Manager");

  const settings = store?.settings || {};
  const tz = safeString(settings?.timezone).trim() || "America/Los_Angeles";

  const ros = useMemo(() => {
    if (Array.isArray(store?.repairOrders)) return store.repairOrders;
    if (Array.isArray(store?.ros)) return store.ros;
    return [];
  }, [store?.repairOrders, store?.ros]);



  const snapshots = useMemo(() => {
    return store?.snapshots || settings?.snapshots || {};
  }, [store?.snapshots, settings?.snapshots]);

  useEffect(() => {
    let alive = true;

    async function refreshDashboardData() {
      if (!alive) return;

      if (typeof store?.refreshStoreData === "function") {
        try {
          await store.refreshStoreData();
          setLastUpdatedISO(new Date().toISOString());
        } catch {
          // keep dashboard usable even if refresh fails
        }
      }
    }

    refreshDashboardData();

    return () => {
      alive = false;
    };
  }, [store]);

  const todayKey = useMemo(() => dateKeyInTZ(new Date(), tz), [tz]);
    const [threadROId, setThreadROId] = useState(null);
  const currentUnreadStoreId = safeString(
    auth?.effectiveStoreId || auth?.activeStoreId
  ).trim();
  const unreadStoreMatches =
    !!currentUnreadStoreId &&
    safeString(unreadStoreId).trim() === currentUnreadStoreId;

  /*
    IMPORTANT:
    Manager Dashboard must match the WIP Board's visible operational reality.
    We still prefer the canonical context buckets, but defensively remove old
    picked-up / vehicle-gone rows because those records can remain in raw data
    while the WIP Board correctly hides them.
  */
  const dashboardOpenROs = useMemo(() => {
    const source = Array.isArray(store?.managerDashboardROs)
      ? store.managerDashboardROs
      : Array.isArray(store?.visibleBoardROs)
      ? store.visibleBoardROs
      : Array.isArray(store?.visibleWipROs)
      ? store.visibleWipROs
      : Array.isArray(store?.wipROs)
      ? store.wipROs
      : Array.isArray(store?.openROs)
      ? store.openROs
      : ros.filter((r) => isDashboardOpenRO(r, store));

    return source.filter((r) => shouldShowOnDashboardWip(r, store, todayKey, tz));
  }, [store, ros, todayKey, tz]);
    const threadRO = useMemo(() => {
    if (!threadROId) return null;

    return (
      dashboardOpenROs.find((ro) => String(ro?.id) === String(threadROId)) ||
      ros.find((ro) => String(ro?.id) === String(threadROId)) ||
      null
    );
  }, [threadROId, dashboardOpenROs, ros]);

  const hasUnreadThreadNote = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      return !!(unreadHydrated && unreadStoreMatches && roId && hasUnread(roId));
    },
    [hasUnread, unreadHydrated, unreadStoreMatches]
  );

  const openThreadForRo = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      if (!roId) return;

      setThreadROId(roId);
    },
    []
  );

  const closeThread = useCallback(() => {
    setThreadROId(null);
  }, []);

  const [exportOpen, setExportOpen] = useState(false);
  const [exportKpiChecked, setExportKpiChecked] = useState(true);
  const [exportOpenWipChecked, setExportOpenWipChecked] = useState(true);
  const [exportPrepBusy, setExportPrepBusy] = useState(false);

  const [dismissedMap, setDismissedMap] = useState({});
  const [managerAttentionSyncError, setManagerAttentionSyncError] = useState("");
  const [managerAttentionLastSyncISO, setManagerAttentionLastSyncISO] = useState("");
  const [snoozeSaving, setSnoozeSaving] = useState(false);
  const [ackModalOpen, setAckModalOpen] = useState(false);
  const [ackTarget, setAckTarget] = useState(null);
  const [ackReason, setAckReason] = useState(ACK_REASONS[0]);
  const [ackOtherText, setAckOtherText] = useState("");
  const [snoozeMode, setSnoozeMode] = useState("TOMORROW");
  const [customSnoozeInput, setCustomSnoozeInput] = useState(
    toLocalDateTimeInputValue(addDays(1))
  );

  const [kpiPreset, setKpiPreset] = useState("TODAY");
  const [customFrom, setCustomFrom] = useState(todayKey);
  const [customTo, setCustomTo] = useState(todayKey);

  const [lastUpdatedISO, setLastUpdatedISO] = useState(() =>
    new Date().toISOString()
  );
  const [wipFilter, setWipFilter] = useState("ALL");
  const [wipSort, setWipSort] = useState("AGE_ASC");
  const [healthExplainerOpen, setHealthExplainerOpen] = useState(false);

  const refreshSharedSnoozes = useCallback(async () => {
    if (typeof apiGet !== "function") return {};

    try {
      const data = await apiGet(
        `/api/manager-attention?_ts=${encodeURIComponent(Date.now())}`
      );
      const rows = Array.isArray(data?.snoozes) ? data.snoozes : [];
      const nextMap = cleanupExpiredSnoozes(sharedSnoozeMap(rows));

      setDismissedMap(nextMap);
      setManagerAttentionSyncError("");
      setManagerAttentionLastSyncISO(
        safeString(data?.serverTime).trim() || new Date().toISOString()
      );

      return nextMap;
    } catch (e) {
      const message = safeString(e?.message || e).trim();
      setManagerAttentionSyncError(
        message || "Shared manager alert state could not be refreshed."
      );
      console.warn("Manager attention sync failed:", message || e);
      return null;
    }
  }, [apiGet]);

  useEffect(() => {
    refreshSharedSnoozes();

    const intervalId = window.setInterval(() => {
      refreshSharedSnoozes();
    }, 5000);

    function onFocus() {
      refreshSharedSnoozes();
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        refreshSharedSnoozes();
      }
    }

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshSharedSnoozes]);

  useEffect(() => {
    if (!ackModalOpen || !ackTarget) return;
    const defaultMode = getDefaultSnoozeMode(ackReason, ackTarget.category);
    setSnoozeMode(defaultMode);
  }, [ackReason, ackModalOpen, ackTarget]);

  function openAcknowledgeModal(item) {
    const defaultReason = ACK_REASONS[0];
    const defaultMode = getDefaultSnoozeMode(defaultReason, item?.category);

    setAckTarget(item || null);
    setAckReason(defaultReason);
    setAckOtherText("");
    setSnoozeMode(defaultMode);
    setCustomSnoozeInput(toLocalDateTimeInputValue(addDays(1)));
    setAckModalOpen(true);
  }

  function closeAcknowledgeModal() {
    if (snoozeSaving) return;
    setAckModalOpen(false);
    setAckTarget(null);
    setAckReason(ACK_REASONS[0]);
    setAckOtherText("");
    setSnoozeMode("TOMORROW");
    setCustomSnoozeInput(toLocalDateTimeInputValue(addDays(1)));
  }

  async function saveAcknowledgement() {
    if (!ackTarget?.alertKey || typeof apiPost !== "function") return;

    const finalReason =
      ackReason === "Other" ? safeString(ackOtherText).trim() : ackReason;

    if (!finalReason) {
      alert("Please enter a snooze reason.");
      return;
    }

    const customIso =
      snoozeMode === "CUSTOM" ? parseLocalDateTimeInput(customSnoozeInput) : "";

    if (snoozeMode === "CUSTOM" && !customIso) {
      alert("Please select a valid custom date/time.");
      return;
    }

    if (snoozeMode === "CUSTOM") {
      const customTime = new Date(customIso).getTime();
      if (!Number.isFinite(customTime) || customTime <= Date.now()) {
        alert("Custom snooze time must be in the future.");
        return;
      }
    }

    const snoozeUntil = resolveSnoozeUntil(snoozeMode, customIso);

    setSnoozeSaving(true);

    try {
      const data = await apiPost("/api/manager-attention", {
        alertKey: ackTarget.alertKey,
        category: ackTarget.category,
        roKey: ackTarget.roKey,
        roNumber: safeString(ackTarget.ro?.roNumber).trim(),
        tagNumber: safeString(ackTarget.ro?.tagNumber).trim(),
        customerName: safeString(ackTarget.ro?.customerName).trim(),
        vehicle: safeString(ackTarget.ro?.vehicle).trim(),
        reason: finalReason,
        snoozeMode,
        snoozeUntil,
      });

      const saved = normalizeSharedSnooze(data?.snooze);
      if (saved.alertKey) {
        setDismissedMap((prev) => ({
          ...prev,
          [saved.alertKey]: saved,
        }));
      }

      const verifiedMap = await refreshSharedSnoozes();
      const verified = findSharedSnoozeForAlert(verifiedMap || {}, {
        alertKey: ackTarget.alertKey,
        category: ackTarget.category,
        ro: ackTarget.ro,
      });

      if (!verified) {
        throw new Error(
          "Flowtrak saved the snooze but could not verify shared manager sync. Please try again."
        );
      }

      setAckModalOpen(false);
      setAckTarget(null);
      setAckReason(ACK_REASONS[0]);
      setAckOtherText("");
      setSnoozeMode("TOMORROW");
      setCustomSnoozeInput(toLocalDateTimeInputValue(addDays(1)));
    } catch (e) {
      alert(e?.message || "Unable to save the shared snooze.");
    } finally {
      setSnoozeSaving(false);
    }
  }

  async function restoreSnooze(alertKey) {
    const key = safeString(alertKey).trim();
    if (!key || typeof apiDelete !== "function") return;

    const previous = dismissedMap[key];

    setDismissedMap((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    try {
      await apiDelete(`/api/manager-attention/${encodeURIComponent(key)}`);
      await refreshSharedSnoozes();
    } catch (e) {
      if (previous) {
        setDismissedMap((prev) => ({ ...prev, [key]: previous }));
      }
      alert(e?.message || "Unable to restore the shared snooze.");
    }
  }

  const operational = useMemo(() => {
    const open = dashboardOpenROs;

    const completedToday = ros.filter((r) => {
      if (!isDashboardCompletedRO(r, store)) return false;

      const completedAt = getCompletedISO(r) || r?.updatedAt || r?.timestamps?.updatedAt || "";
      return isSameDateKeyTZ(completedAt, todayKey, tz);
    });

    const waitingOnParts = open.filter((r) => isDashboardWaitingForParts(r, store));
    const unassigned = open.filter((r) =>
      isDashboardWaitingForAssignment(r, store)
    );
    const inProgress = open.filter((r) => isDashboardInProgress(r, store));
    const waitersOpen = open.filter((r) => isWaiterRO(r));

    const promiseRisk = open.filter((r) => {
      if (isDashboardCustomerContacted(r)) return false;
      const p = getPromiseISO(r);
      const m = minutesUntil(p);
      if (m == null) return false;
      return m <= 60;
    });

    return {
      openWip: open.length,
      completedToday: completedToday.length,
      waitingOnParts: waitingOnParts.length,
      unassigned: unassigned.length,
      inProgress: inProgress.length,
      waitersOpen: waitersOpen.length,
      promiseRisk: promiseRisk.length,
    };
  }, [ros, dashboardOpenROs, store, todayKey, tz]);

  const topStates = useMemo(() => {
    const open = dashboardOpenROs;

    const byAgeAnchorAsc = (a, b) => {
      const ta = new Date(ageAnchorISOForRO(a) || 0).getTime();
      const tb = new Date(ageAnchorISOForRO(b) || 0).getTime();
      return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
    };

    const isUnassigned = (r) => {
      const lc = safeString(r?.lifecycle || r?.status).toUpperCase();
      return lc === "WAITING_FOR_ASSIGNMENT" || lc === "UNASSIGNED";
    };

    const isPartsWait = (r) => isDashboardWaitingForParts(r, store);

    const isInProgress = (r) => {
      const lc = safeString(r?.lifecycle || r?.status).toUpperCase();
      return lc === "IN_PROGRESS";
    };

    const unassigned = [...open].filter(isUnassigned).sort(byAgeAnchorAsc);
    const partsWait = [...open].filter(isPartsWait).sort(byAgeAnchorAsc);
    const inProgress = [...open].filter(isInProgress).sort(byAgeAnchorAsc);

    const withPromise = open
      .filter((r) => !isDashboardCustomerContacted(r))
      .map((r) => ({ r, m: minutesUntil(getPromiseISO(r)) }))
      .filter((x) => x.m != null && x.m <= 60)
      .sort((a, b) => a.m - b.m);

    return {
      oldestUnassigned: unassigned[0] || null,
      oldestParts: partsWait[0] || null,
      stalledInProgress: inProgress[0] || null,
      mostOverdue: withPromise[0]?.r || null,
      mostOverdueMins: withPromise[0]?.m ?? null,
    };
   }, [dashboardOpenROs, store]);

  const actionEngine = useMemo(() => {
    const candidates = [];

    const pushItem = ({
      alertKey,
      category,
      label,
      owner,
      actionHint,
      ro,
      rightText,
      danger,
      priority,
    }) => {
      if (!ro || !alertKey) return;

      const dismissed = findSharedSnoozeForAlert(dismissedMap, {
        alertKey,
        category,
        ro,
      });
      const snoozed = !!dismissed;

      candidates.push({
        alertKey,
        category,
        label,
        owner,
        actionHint,
        ro,
        rightText,
        danger,
        priority,
        roKey: roKeyForNavigate(ro),
        snoozed,
        snoozeEntry: dismissed,
      });
    };

    if (topStates.mostOverdue) {
      const ro = topStates.mostOverdue;
      const waiter = isWaiterRO(ro);
      const attentionIdentity = roAttentionIdentity(ro);

      pushItem({
        alertKey: `PROMISE_RECOVERY::${attentionIdentity}`,
        category: "PROMISE_RECOVERY",
        label: waiter ? "Waiter promise at risk" : "Most overdue promise",
        owner: "Advisor",
        actionHint: waiter
          ? "Update customer / prioritize waiter"
          : "Update customer / re-promise",
        ro,
        rightText: promiseDisplayLabel(getPromiseISO(ro), tz),
        danger: topStates.mostOverdueMins != null && topStates.mostOverdueMins <= 0,
        priority: waiter ? 110 : 100,
      });
    }

    if (topStates.oldestUnassigned) {
      const ro = topStates.oldestUnassigned;
      const waiter = isWaiterRO(ro);
      const attentionIdentity = roAttentionIdentity(ro);

      pushItem({
        alertKey: `ASSIGNMENT_DELAY::${attentionIdentity}`,
        category: "ASSIGNMENT_DELAY",
        label: waiter ? "Waiter awaiting assignment" : "Oldest unassigned",
        owner: "Dispatcher",
        actionHint: waiter
          ? "Assign technician / prioritize waiter"
          : "Assign technician",
        ro,
        rightText: agingLabelForRO(ro),
        danger: agingDangerForRO(ro),
        priority: waiter ? 95 : agingDangerForRO(ro) ? 90 : 50,
      });
    }

    if (topStates.oldestParts) {
      const ro = topStates.oldestParts;
      const waiter = isWaiterRO(ro);
      const attentionIdentity = roAttentionIdentity(ro);

      pushItem({
        alertKey: `PARTS_DELAY::${attentionIdentity}`,
        category: "PARTS_DELAY",
        label: waiter ? "Waiter held for parts" : "Oldest waiting parts",
        owner: "Parts",
        actionHint: waiter
          ? "Check ETA / escalate immediately"
          : "Check ETA / escalate parts",
        ro,
        rightText: agingLabelForRO(ro),
        danger: agingDangerForRO(ro),
        priority: waiter ? 85 : agingDangerForRO(ro) ? 80 : 40,
      });
    }

    if (topStates.stalledInProgress) {
      const ro = topStates.stalledInProgress;
      const waiter = isWaiterRO(ro);
      const stalledDanger = agingDangerForRO(ro);
      const attentionIdentity = roAttentionIdentity(ro);

      if (stalledDanger) {
        pushItem({
          alertKey: `IN_PROGRESS_AGING::${attentionIdentity}`,
          category: "IN_PROGRESS_AGING",
          label: waiter ? "Waiter stalled in progress" : "Stalled in progress",
          owner: "Shop Foreman",
          actionHint: waiter
            ? "Unblock technician / protect waiter"
            : "Check stall reason / confirm progress",
          ro,
          rightText: agingLabelForRO(ro),
          danger: true,
          priority: waiter ? 88 : 70,
        });
      }
    }

    const actionable = candidates
      .filter((item) => !item.snoozed)
      .sort((a, b) => b.priority - a.priority);

    const snoozed = candidates
      .filter((item) => item.snoozed)
      .sort((a, b) => b.priority - a.priority);

    return {
      allCandidates: candidates,
      actionableItems: actionable,
      visibleActionItems: actionable.slice(0, 4),
      snoozedCandidates: snoozed,
    };
  }, [topStates, tz, dismissedMap]);

  const actionItems = actionEngine.visibleActionItems;

  const activeSnoozes = useMemo(() => {
    const byStoredKey = new Map();

    actionEngine.snoozedCandidates.forEach((candidate) => {
      const entry = candidate?.snoozeEntry;
      if (!entry || !isSuppressed(entry)) return;

      const storedKey =
        safeString(entry.alertKey).trim() || safeString(candidate.alertKey).trim();
      if (!storedKey) return;

      byStoredKey.set(storedKey, {
        ...entry,
        alertKey: storedKey,
        currentAlertKey: candidate.alertKey,
      });
    });

    return Array.from(byStoredKey.values()).sort((a, b) => {
      const ta = new Date(a.snoozeUntil || 0).getTime();
      const tb = new Date(b.snoozeUntil || 0).getTime();
      return ta - tb;
    });
  }, [actionEngine.snoozedCandidates]);

  const patternAlerts = useMemo(() => {
    const open = dashboardOpenROs;
    const alerts = [];

    const activeDismissals = activeSnoozes;

    const reasonCounts = activeDismissals.reduce((acc, entry) => {
      const reason = safeString(entry?.reason).trim();
      if (!reason) return acc;
      acc[reason] = (acc[reason] || 0) + 1;
      return acc;
    }, {});

    Object.entries(reasonCounts).forEach(([reason, count]) => {
      if (count >= 2) {
        alerts.push({
          key: `ack-${reason}`,
          title: `${count} snoozed item${count === 1 ? "" : "s"}: ${reason}`,
          subtitle: "Repeated snoozes may indicate a broader process issue.",
          priority: 95,
        });
      }
    });

    const overdue = open.filter((r) => {
      if (isDashboardCustomerContacted(r)) return false;
      const mins = minutesUntil(getPromiseISO(r));
      return mins != null && mins < 0;
    });

    if (overdue.length >= 2) {
      alerts.push({
        key: "overdue-pattern",
        title: `${overdue.length} repair orders are already overdue`,
        subtitle: "These promises need customer updates or new promise times.",
        priority: 100,
      });
    }

    const waitersOpen = open.filter((r) => isWaiterRO(r));
    if (waitersOpen.length >= 2) {
      alerts.push({
        key: "waiter-pattern",
        title: `${waitersOpen.length} waiters are still open`,
        subtitle:
          "Waiting customers should be reviewed first.",
        priority: 85,
      });
    }

    const partsWait = open.filter((r) => {
      const lc = safeString(r?.lifecycle || r?.status).toUpperCase();
      return lc === "WAITING_FOR_PARTS" || lc === "WAITING_ON_PARTS";
    });

    if (partsWait.length >= 2) {
      alerts.push({
        key: "parts-pattern",
        title: `${partsWait.length} repair orders are waiting on parts`,
        subtitle: "Parts follow-up may affect throughput.",
        priority: 80,
      });
    }

    return alerts.sort((a, b) => b.priority - a.priority).slice(0, 3);
  }, [dashboardOpenROs, activeSnoozes]);

  const attentionSummary = useMemo(() => {
    const actionable = actionEngine.actionableItems;
    const snoozed = actionEngine.snoozedCandidates;

    const actionablePromise = actionable.filter(
      (item) => item.category === "PROMISE_RECOVERY"
    ).length;
    const actionableAssign = actionable.filter(
      (item) => item.category === "ASSIGNMENT_DELAY"
    ).length;
    const actionableParts = actionable.filter(
      (item) => item.category === "PARTS_DELAY"
    ).length;
    const actionableStalled = actionable.filter(
      (item) => item.category === "IN_PROGRESS_AGING"
    ).length;

    return {
      actionableCount: actionable.length,
      actionableDangerCount: actionable.filter((item) => item.danger).length,
      actionablePromise,
      actionableAssign,
      actionableParts,
      actionableStalled,
      snoozedCount: snoozed.length,
    };
  }, [actionEngine]);

  const attentionItems = useMemo(() => {
    const items = [];

    if (attentionSummary.actionablePromise > 0) {
      items.push({
        text: `${attentionSummary.actionablePromise === 1 ? "Promise issue" : "Promise issues"}: ${attentionSummary.actionablePromise}`,
        danger: attentionSummary.actionablePromise >= 1,
      });
    }

    if (attentionSummary.actionableAssign > 0) {
      items.push({
        text: `${attentionSummary.actionableAssign === 1 ? "Assignment issue" : "Assignment issues"}: ${attentionSummary.actionableAssign}`,
        danger: attentionSummary.actionableAssign >= 1,
      });
    }

    if (attentionSummary.actionableParts > 0) {
      items.push({
        text: `${attentionSummary.actionableParts === 1 ? "Parts issue" : "Parts issues"}: ${attentionSummary.actionableParts}`,
        danger: attentionSummary.actionableParts >= 1,
      });
    }

    if (attentionSummary.actionableStalled > 0) {
      items.push({
        text: `${attentionSummary.actionableStalled === 1 ? "Stalled job" : "Stalled jobs"}: ${attentionSummary.actionableStalled}`,
        danger: attentionSummary.actionableStalled >= 1,
      });
    }

    if (!items.length && attentionSummary.snoozedCount > 0) {
      items.push({
        text: `${attentionSummary.snoozedCount} tracked issue${
          attentionSummary.snoozedCount === 1 ? "" : "s"
        } snoozed below`,
        danger: false,
      });
    }

    return items;
  }, [attentionSummary]);

  const attentionState = useMemo(() => {
    if (attentionSummary.actionableCount > 0) {
      const hasDanger = attentionSummary.actionableDangerCount > 0;
      return {
        variant: hasDanger ? "danger" : "warn",
        title: hasDanger ? "Manager attention" : "Watch list",
        subtitle: "The repair orders most likely to affect today’s flow, promises, or customer communication.",
      };
    }

    if (attentionSummary.snoozedCount > 0) {
      return {
        variant: "good",
        title: "No unsnoozed manager alerts",
        subtitle: `${attentionSummary.snoozedCount} tracked issue${
          attentionSummary.snoozedCount === 1 ? " is" : "s are"
        } snoozed and listed below.`,
      };
    }

    return {
      variant: "good",
      title: "Shop flow is on track",
      subtitle: `No immediate pressure points detected as of ${formatTimeShort(
        lastUpdatedISO
      )}.`,
    };
  }, [attentionSummary, lastUpdatedISO]);

  const executiveInsights = useMemo(() => {
    const overdueCount = dashboardOpenROs.filter((r) => {
      if (isDashboardCustomerContacted(r)) return false;
      const mins = minutesUntil(getPromiseISO(r));
      return mins != null && mins < 0;
    }).length;

    const openWip = operational.openWip;
    const unassigned = operational.unassigned;
    const waiters = operational.waitersOpen;
    const inProgress = operational.inProgress;
    const waitingParts = operational.waitingOnParts;
    const promiseRisk = operational.promiseRisk;

    let score = 100;

    score -= Math.min(35, overdueCount * 3);
    score -= Math.min(25, unassigned * 2);
    score -= Math.min(15, waiters * 3);
    score -= Math.min(15, promiseRisk * 2);
    score -= Math.min(10, waitingParts * 2);

    if (openWip >= 20 && inProgress === 0) score -= 15;

    score = Math.max(0, Math.min(100, score));

    const health =
      score < 55
        ? { label: "Needs Attention", tone: "danger" }
        : score < 78
        ? { label: "Watch", tone: "warn" }
        : { label: "Healthy", tone: "good" };

    const cards = [];

    if (overdueCount > 0) {
      cards.push({
        key: "promise",
        title: "Promise recovery",
        value: overdueCount,
        unit: "overdue",
        tone: overdueCount >= 3 ? "danger" : "warn",
        body:
          overdueCount >= 3
            ? "Customers need updates and new promise times."
            : "One customer promise needs an update.",
      });
    }

    if (unassigned > 0) {
      cards.push({
        key: "assignment",
        title: "Dispatch ownership",
        value: unassigned,
        unit: "unassigned",
        tone: unassigned >= 3 ? "danger" : "warn",
        body:
          unassigned >= 3
            ? "Unassigned work is causing a bottleneck."
            : "A small assignment queue is forming.",
      });
    }

    if (openWip >= 8 && inProgress === 0) {
      cards.push({
        key: "flow",
        title: "Work flow concern",
        value: inProgress,
        unit: "in progress",
        tone: "danger",
        body: `${openWip} open ROs with no active work may indicate stalled shop flow.`,
      });
    }

    if (waiters > 0) {
      cards.push({
        key: "waiters",
        title: "Waiting customers",
        value: waiters,
        unit: "waiters",
        tone: waiters >= 2 ? "danger" : "warn",
        body:
          waiters >= 2
            ? "Several waiters need updates."
            : "One waiter needs an update.",
      });
    }

    if (waitingParts > 0) {
      cards.push({
        key: "parts",
        title: "Parts follow-up",
        value: waitingParts,
        unit: "waiting parts",
        tone: waitingParts >= 4 ? "danger" : "warn",
        body:
          waitingParts >= 4
            ? "Parts follow-up needs attention."
            : "Parts follow-up needs attention.",
      });
    }

    if (!cards.length) {
      cards.push({
        key: "stable",
        title: "No major bottleneck detected",
        value: "✓",
        unit: "stable",
        tone: "good",
        body: "Current WIP does not show an immediate operational pressure point.",
      });
    }

    const bottlenecks = [
      {
        label: "Promise recovery",
        count: overdueCount,
        tone: overdueCount >= 3 ? "danger" : overdueCount > 0 ? "warn" : "neutral",
      },
      {
        label: "Dispatch assignment",
        count: unassigned,
        tone: unassigned >= 3 ? "danger" : unassigned > 0 ? "warn" : "neutral",
      },
      {
        label: "Waiting customers",
        count: waiters,
        tone: waiters >= 2 ? "danger" : waiters > 0 ? "warn" : "neutral",
      },
      {
        label: "Parts follow-up",
        count: waitingParts,
        tone: waitingParts >= 4 ? "danger" : waitingParts > 0 ? "warn" : "neutral",
      },
      {
        label: "Active work flow",
        count: openWip >= 8 && inProgress === 0 ? openWip : 0,
        tone: openWip >= 8 && inProgress === 0 ? "danger" : "neutral",
      },
    ]
      .filter((x) => x.count > 0)
      .slice(0, 4);

    const scoreDrivers = [
      {
        label: "Overdue promises",
        value: overdueCount,
        impact: `-${Math.min(35, overdueCount * 3)}`,
        tone: overdueCount >= 3 ? "danger" : overdueCount > 0 ? "warn" : "good",
        note: "Broken or near-broken customer promises reduce shop health quickly.",
        action: "Contact customers and update promise times immediately.",
      },
      {
        label: "Unassigned repair orders",
        value: unassigned,
        impact: `-${Math.min(25, unassigned * 2)}`,
        tone: unassigned >= 3 ? "danger" : unassigned > 0 ? "warn" : "good",
        note: "Unassigned work signals a dispatch backlog and lack of dispatchflow.",
        action: "Assign technicians to reduce dispatch backlog.",
      },
      {
        label: "Waiters open",
        value: waiters,
        impact: `-${Math.min(15, waiters * 3)}`,
        tone: waiters >= 2 ? "danger" : waiters > 0 ? "warn" : "good",
        note: "Waiting customers create immediate service lane pressure.",
        action: "Review waiting customers and move their ROs forward.",
      },
      {
        label: "Promise risk",
        value: promiseRisk,
        impact: `-${Math.min(15, promiseRisk * 2)}`,
        tone: promiseRisk >= 3 ? "danger" : promiseRisk > 0 ? "warn" : "good",
        note: "ROs due soon or overdue need proactive attention.",
        action: "Prioritize near-term promises and communicate updates.",
      },
      {
        label: "Waiting parts",
        value: waitingParts,
        impact: `-${Math.min(10, waitingParts * 2)}`,
        tone: waitingParts >= 4 ? "danger" : waitingParts > 0 ? "warn" : "good",
        note: "Parts delays can block throughput when they build up.",
        action: "Follow up on required parts and verify ETA.",
      },
      {
        label: "Flow stall",
        value: openWip >= 8 && inProgress === 0 ? "Yes" : "No",
        impact: openWip >= 8 && inProgress === 0 ? "-15" : "0",
        tone: openWip >= 8 && inProgress === 0 ? "danger" : "good",
        note: "High open WIP with no active work means the shop may not be moving.",
        action: "Start work on vehicles or verify technician activity.",
      },
    ];

    const worstDriver = [...scoreDrivers]
      .filter((d) => String(d.impact).startsWith("-"))
      .sort((a, b) => Number(a.impact) - Number(b.impact))[0];

    const primaryBottleneck = bottlenecks[0]?.label || "No major bottleneck";

    let recommendedAction = "Maintain flow and monitor active repair orders.";

    if (primaryBottleneck === "Promise recovery") {
      recommendedAction =
        "Start with the most overdue RO and work down the list — contact customers and reset expectations immediately.";
    } else if (primaryBottleneck === "Dispatch assignment") {
      recommendedAction =
        "Assign the 3 oldest unassigned ROs first to regain control of dispatch flow.";
    } else if (primaryBottleneck === "Waiting customers") {
      recommendedAction =
        "Immediately prioritize all waiters and verify technician engagement to reduce front-drive pressure.";
    } else if (primaryBottleneck === "Parts follow-up") {
      recommendedAction =
        "Review all waiting-parts ROs and confirm ETA or escalate any delayed orders.";
    } else if (primaryBottleneck === "Active work flow") {
      recommendedAction =
        "Begin work on idle ROs and confirm all technicians are actively engaged.";
    }

    const urgency =
      score < 55
        ? "Today's focus"
        : score < 78
        ? "Monitor closely"
        : "On track";

    const dailySummary = {
      title: "Daily Shop Brief",
      narrative:
        `Current shop health is ${health.label}. ` +
        `${openWip} open ROs: ${overdueCount} overdue, ${unassigned} unassigned, and ${inProgress} in progress. ` +
        `Primary focus: ${primaryBottleneck.toLowerCase()}.`,
      recommendedAction,
      urgency,
    };

    return {
      score,
      health,
      cards: cards.slice(0, 4),
      bottlenecks,
      scoreDrivers,
      worstDriver,
      primaryBottleneck,
      dailySummary,
    };
  }, [dashboardOpenROs, operational]);

  const openROs = useMemo(() => {
    const open = dashboardOpenROs;

    const filtered = open.filter((r) => {
      const transport = safeString(r?.transportType).toUpperCase();
      const promiseISO = getPromiseISO(r);
      const m = minutesUntil(promiseISO);

      if (wipFilter === "ASSIGN") {
        return isDashboardWaitingForAssignment(r, store);
      }
      if (wipFilter === "PARTS") {
        return isDashboardWaitingForParts(r, store);
      }
      if (wipFilter === "PROG") return isDashboardInProgress(r, store);
      if (wipFilter === "WAITER") return transport === "WAITER";
      if (wipFilter === "RISK") return !isDashboardCustomerContacted(r) && m != null && m <= 60;

      return true;
    });

    const copy = [...filtered];
    copy.sort((a, b) => {
      const createdA = createdTimeForRO(a);
      const createdB = createdTimeForRO(b);

      if (wipSort === "AGE_DESC") {
        return createdA - createdB;
      }

      if (wipSort === "AGE_ASC") {
        return createdB - createdA;
      }

      if (wipSort === "PROMISE") {
        const promiseA = new Date(getPromiseISO(a) || 0).getTime();
        const promiseB = new Date(getPromiseISO(b) || 0).getTime();

        return (
          (Number.isFinite(promiseA) && promiseA > 0
            ? promiseA
            : Number.POSITIVE_INFINITY) -
          (Number.isFinite(promiseB) && promiseB > 0
            ? promiseB
            : Number.POSITIVE_INFINITY)
        );
      }

      const ma = minutesUntil(getPromiseISO(a));
      const mb = minutesUntil(getPromiseISO(b));

      const overdueA = ma != null && ma < 0 ? 1 : 0;
      const overdueB = mb != null && mb < 0 ? 1 : 0;
      if (overdueA !== overdueB) return overdueB - overdueA;

      const riskA = ma != null && ma <= 60 ? 1 : 0;
      const riskB = mb != null && mb <= 60 ? 1 : 0;
      if (riskA !== riskB) return riskB - riskA;

      const waiterA =
        safeString(a?.transportType).toUpperCase() === "WAITER" ? 1 : 0;
      const waiterB =
        safeString(b?.transportType).toUpperCase() === "WAITER" ? 1 : 0;
      if (waiterA !== waiterB) return waiterB - waiterA;

      return createdA - createdB;
    });

    return copy;
  }, [dashboardOpenROs, store, wipFilter, wipSort]);

  const kpiRange = useMemo(() => {
    const now = new Date();
    const today = dateKeyInTZ(now, tz);

    if (kpiPreset === "TODAY") return { from: today, to: today };

    if (kpiPreset === "PAST_7") {
      const d = new Date(now);
      d.setDate(d.getDate() - 6);
      return { from: dateKeyInTZ(d, tz), to: today };
    }

    if (kpiPreset === "THIS_MONTH") {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: dateKeyInTZ(d, tz), to: today };
    }

    return { from: customFrom, to: customTo };
  }, [kpiPreset, customFrom, customTo, tz]);

  const kpiFiltered = useMemo(() => {
    const minISO = startOfDayISO(kpiRange.from);
    const maxISO = endOfDayISO(kpiRange.to);

    return ros.filter((r) => {
      const when =
        r?.createdAt || r?.openedAt || r?.inAt || r?.updatedAt || r?.arrivedAt;
      if (!when) return false;

      const d = new Date(when);
      if (Number.isNaN(d.getTime())) return false;

      const iso = d.toISOString();
      return iso >= minISO && iso <= maxISO;
    });
  }, [ros, kpiRange.from, kpiRange.to]);

  const kpiRows = useMemo(
    () => kpiFiltered.map((r) => normalizeKpiRow(r)),
    [kpiFiltered]
  );

  const trendDays = useMemo(() => {
    const out = [];
    const base = new Date();
    base.setHours(0, 0, 0, 0);

    for (let i = 13; i >= 0; i--) {
      const d = new Date(base);
      d.setDate(d.getDate() - i);
      const key = dateKeyInTZ(d, tz);
      const snap = snapshots?.[key] || null;

      out.push({
        dateKey: key,
        wipCount: safeNum(snap?.wipCount ?? snap?.openWip ?? 0),
        waiterCount: safeNum(snap?.waiterCount ?? 0),
        waitingOnPartsCount: safeNum(snap?.waitingOnPartsCount ?? 0),
        dispatchCount: safeNum(snap?.dispatchCount ?? 0),
        assignedCount: safeNum(snap?.assignedCount ?? 0),
        inProgressCount: safeNum(snap?.inProgressCount ?? 0),
        capturedAtISO: safeString(snap?.capturedAtISO || snap?.captured_at || ""),
        timezone: safeString(snap?.timezone || tz),
        has: !!snap,
      });
    }

    return out;
  }, [snapshots, tz]);

  const trendDelta = useMemo(() => {
    const last = trendDays[trendDays.length - 1];
    const prev = trendDays[trendDays.length - 2];
    if (!last || !prev) return 0;
    return safeNum(last.wipCount) - safeNum(prev.wipCount);
  }, [trendDays]);

  const trendStats = useMemo(() => {
    const values = trendDays.filter((d) => d.has).map((d) => safeNum(d.wipCount));
    const max = Math.max(...values, 0);
    const min = values.length ? Math.min(...values) : 0;
    return {
      max: max > 0 ? max : 1,
      min,
      range: Math.max(1, max - min),
    };
  }, [trendDays]);

  const latestSnapshot = useMemo(() => {
    const entries = Object.values(snapshots || {})
      .filter((snap) => snap && typeof snap === "object" && safeString(snap.dateKey).trim())
      .sort((a, b) => safeString(b.dateKey).localeCompare(safeString(a.dateKey)));

    return entries[0] || null;
  }, [snapshots]);

  const todaySnapshot = useMemo(() => {
    return snapshots?.[todayKey] || null;
  }, [snapshots, todayKey]);

  const snapshotCoverage = useMemo(() => {
    return trendDays.filter((d) => d.has).length;
  }, [trendDays]);

  const snapshotStatus = useMemo(() => {
    if (todaySnapshot) {
      return {
        label: "Current",
        tone: "good",
        text: `Today captured at ${formatDateTimeDisplay(
          todaySnapshot.capturedAtISO || todaySnapshot.captured_at,
          tz
        )}.`,
      };
    }

    if (latestSnapshot) {
      return {
        label: "Waiting for midnight",
        tone: "warn",
        text: `Latest snapshot is ${safeString(latestSnapshot.dateKey).trim()}. Today will populate after the store's midnight capture.`,
      };
    }

    return {
      label: "No history yet",
      tone: "neutral",
      text: "Historical trend data will appear after the first midnight capture.",
    };
  }, [latestSnapshot, todaySnapshot, tz]);

  async function openExportModal() {
    if (exportPrepBusy) return;
    setExportPrepBusy(true);

    try {
      const hasToday = !!snapshots?.[todayKey];

      if (!hasToday && typeof store?.captureWipSnapshot === "function") {
        await store.captureWipSnapshot(todayKey, { force: false });
        setLastUpdatedISO(new Date().toISOString());
      }
    } catch {
      // keep UX smooth
    } finally {
      setExportPrepBusy(false);
      setExportOpen(true);
    }
  }

  async function exportSelected() {
    const didSelect = !!exportKpiChecked || !!exportOpenWipChecked;
    if (!didSelect) {
      alert("Select at least one report to export.");
      return;
    }

    try {
      if (exportKpiChecked) {
        const filename = `flowtrak_kpi_${kpiRange.from}_to_${kpiRange.to}.xlsx`;

        await exportKpiRangeXlsx({
          filename,
          kpiRows,
          allRos: dashboardOpenROs,
          rangeLabel: `${kpiRange.from} → ${kpiRange.to}`,
          stats: {
            count: kpiRows.length,
            openWip: dashboardOpenROs.length,
            completedToday: operational.completedToday,
            waitingOnParts: operational.waitingOnParts,
            unassigned: operational.unassigned,
            inProgress: operational.inProgress,
            waitersOpen: operational.waitersOpen,
            promiseRisk: operational.promiseRisk,
          },
        });
      }

      if (exportOpenWipChecked) {
        const open = dashboardOpenROs;
        const rows = open.map((r) => ({
          ...normalizeWipRow(r),
          createdAtISO: getCreatedISO(r),
          createdAt: getCreatedISO(r),
          createdDate: formatDateTimeDisplay(getCreatedISO(r), tz),
          ageDays: shopAgeDaysForRO(r),
          age: shopAgeLabelForRO(r),
        }));

        await exportWipFilteredXlsx({
          dateLabel: todayKey,
          wipRows: rows,
          counts: {
            openWip: open.length,
          },
        });
      }

      setExportOpen(false);
    } catch (e) {
      alert(e?.message || "Export failed");
    }
  }

  return (
    <div className="ft-page" style={{ padding: 12, gap: 10 }}>
      <div
        className="ft-pageHeader"
        style={{ marginBottom: 2, alignItems: "center" }}
      >
        <div>
          <div className="ft-h1" style={{ fontSize: "1.4rem", lineHeight: 1.08 }}>
            Manager Dashboard
          </div>
          <div className="ft-subtle" style={{ marginTop: 4, fontSize: 11.5 }}>
            Command center for shop flow, promise risk, and team follow-up.
          </div>
        </div>

        <div className="ft-actions">
          <button
            className="ft-btn"
            onClick={openExportModal}
            disabled={exportPrepBusy}
            style={{ padding: "8px 10px", fontSize: 12 }}
          >
            {exportPrepBusy ? "Preparing…" : "Export…"}
          </button>
        </div>
      </div>

      {managerAttentionSyncError ? (
        <div
          className="ft-card"
          style={{
            border: "1px solid rgba(180,35,24,0.16)",
            background: "rgba(254,243,242,0.92)",
            color: "#b42318",
            borderRadius: 12,
            padding: "8px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 11.3, fontWeight: 800 }}>
            Shared manager alert sync is unavailable. Snoozes may not be visible to other managers.
          </div>
          <button
            type="button"
            className="ft-btn ft-btn--ghost"
            onClick={refreshSharedSnoozes}
            style={{ height: 30, padding: "0 10px", fontSize: 11 }}
          >
            Retry sync
          </button>
        </div>
      ) : null}

      <AttentionBar
        state={attentionState}
        items={attentionItems}
        updatedAt={managerAttentionLastSyncISO || lastUpdatedISO}
      />

      <ExecutiveInsightsPanel
        insights={executiveInsights}
        onExplain={() => setHealthExplainerOpen(true)}
      />

      <DailyShopSummary summary={executiveInsights.dailySummary} />

      <div
        className="ft-twoCol ft-managerDashboardLayout"
      >
        <div style={{ display: "grid", gap: 10, minWidth: 0 }}>
          <div
            className="ft-card"
            style={{
              ...dashboardShellCardStyle(),
              paddingTop: 10,
              paddingBottom: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                alignItems: "flex-start",
                flexWrap: "wrap",
                marginBottom: actionItems.length ? 6 : 4,
              }}
            >
              <div>
                <div style={denseSectionTitleStyle()}>Attention Needed</div>
                <div className="ft-caption" style={{ marginTop: 4, fontSize: 11.3 }}>
                  Start here: the repair orders most likely to affect today’s promises, customers, or shop flow.
                </div>
              </div>

              <div className="ft-caption" style={{ marginTop: 0, fontSize: 11.2 }}>
                Showing <b>{actionItems.length}</b> priority item
                {actionItems.length === 1 ? "" : "s"}
              </div>
            </div>

            {actionItems.length === 0 ? (
              <div className="ft-caption" style={{ padding: "6px 2px 0", fontSize: 11.3 }}>
                No active pressure points detected right now.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 7 }}>
                {actionItems.map((item) => (
                  <ActionQueueRow
                    key={item.alertKey}
                    label={item.label}
                    owner={item.owner}
                    actionHint={item.actionHint}
                    ro={item.ro}
                    tz={tz}
                    rightText={item.rightText}
                    danger={item.danger}
                    category={item.category}
                    onJump={(ro, category) => {
                      const key = roKeyForNavigate(ro);

                      let filter = "";

                      if (category === "ASSIGNMENT_DELAY") filter = "ASSIGN";
                      if (category === "PARTS_DELAY") filter = "PARTS";
                      if (category === "IN_PROGRESS_AGING") filter = "PROG";
                      if (category === "PROMISE_RECOVERY") filter = "RISK";

                      if (key) {
                        nav(`/dispatch?tag=${encodeURIComponent(key)}&filter=${filter}`);
                      } else {
                        nav(`/dispatch?filter=${filter}`);
                      }
                    }}
                                        onAcknowledge={() => openAcknowledgeModal(item)}
                    onOpenThread={openThreadForRo}
                    hasUnreadNote={hasUnreadThreadNote(item.ro)}
                  />
                ))}
              </div>
            )}
          </div>

          <div
            className="ft-card"
            style={{
              ...dashboardShellCardStyle(),
              paddingTop: 10,
              paddingBottom: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                alignItems: "flex-start",
                flexWrap: "wrap",
                marginBottom: 6,
              }}
            >
              <div>
                <div style={denseSectionTitleStyle()}>Current WIP</div>
                <div className="ft-caption" style={{ marginTop: 4, fontSize: 11.3 }}>
                  Open repair orders • Click a row to open Dispatch • Showing {openROs.length}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {[
                  ["ALL", "All"],
                  ["ASSIGN", "Unassigned"],
                  ["PARTS", "Waiting Parts"],
                  ["PROG", "In Progress"],
                  ["WAITER", "Waiters"],
                  ["RISK", "Promise Risk"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={wipFilter === key ? "ft-chip ft-chip-active" : "ft-chip"}
                    onClick={() => setWipFilter(key)}
                    style={{ fontSize: 11.1, padding: "6px 9px", height: 30 }}
                  >
                    {label}
                  </button>
                ))}

                <select
                  value={wipSort}
                  onChange={(e) => setWipSort(e.target.value)}
                  title="Sort Current WIP"
                  style={{
                    height: 30,
                    minWidth: 150,
                    padding: "0 9px",
                    borderRadius: 999,
                    border: "1px solid rgba(15,23,42,0.12)",
                    background: "#ffffff",
                    color: "rgba(15,23,42,0.82)",
                    fontSize: 11.1,
                    fontWeight: 900,
                  }}
                >
                  {WIP_SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      Sort: {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              className="ft-tableWrap"
              style={{
                padding: 0,
                maxHeight: 520,
                overflowY: "auto",
                overflowX: "auto",
                borderTop: "1px solid rgba(15,23,42,0.05)",
                marginTop: 2,
              }}
            >
              {openROs.length === 0 ? (
                <div className="ft-caption" style={{ padding: "10px 2px", fontSize: 11.3 }}>
                  No open repair orders.
                </div>
              ) : (
                <table className="ft-table" style={{ fontSize: 10.75, borderSpacing: "0 4px" }}>
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      background: "#f8fafc",
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          fontSize: 9.85,
                          paddingTop: 6,
                          paddingBottom: 6,
                          background: "#f8fafc",
                        }}
                      >
                        RO
                      </th>
                      <th
                        style={{
                          fontSize: 9.85,
                          paddingTop: 6,
                          paddingBottom: 6,
                          background: "#f8fafc",
                        }}
                      >
                        Created
                      </th>
                      <th
                        style={{
                          fontSize: 9.85,
                          paddingTop: 6,
                          paddingBottom: 6,
                          background: "#f8fafc",
                        }}
                      >
                        Age
                      </th>
                      <th
                        style={{
                          fontSize: 9.85,
                          paddingTop: 6,
                          paddingBottom: 6,
                          background: "#f8fafc",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          fontSize: 9.85,
                          paddingTop: 6,
                          paddingBottom: 6,
                          background: "#f8fafc",
                        }}
                      >
                        Transport
                      </th>
                      <th
                        style={{
                          fontSize: 9.85,
                          paddingTop: 6,
                          paddingBottom: 6,
                          background: "#f8fafc",
                        }}
                      >
                        Promise
                      </th>
                      <th
                        style={{
                          fontSize: 9.85,
                          paddingTop: 6,
                          paddingBottom: 6,
                          background: "#f8fafc",
                        }}
                      >
                        Latest Note
                      </th>
                      <th
                        style={{
                          textAlign: "right",
                          fontSize: 9.85,
                          paddingTop: 6,
                          paddingBottom: 6,
                          background: "#f8fafc",
                        }}
                      >
                        Activity
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {openROs.map((ro) => {
                      const openedISO = getOpenedISO(ro);
                      const createdISO = getCreatedISO(ro) || openedISO;
                      const created = formatDateTimeDisplay(createdISO, tz);
                      const shopAge = shopAgeLabelForRO(ro);

                      const operationalStatus = getManagerDashboardOperationalStatus(ro);

                      const transport =
                        safeString(ro?.transportType).toUpperCase() || "OTHER";
                      const promiseISO = getPromiseISO(ro);
                      const mins = minutesUntil(promiseISO);

                      const roNumber = safeString(ro?.roNumber).trim();
                      const tag = safeString(ro?.tagNumber).trim();
                      const keyForJump = roKeyForNavigate(ro);

                      const customer = safeString(ro?.customerName).trim();
                      const vehicle = safeString(ro?.vehicle).trim();

                      const promiseLabel = promiseDisplayLabel(promiseISO, tz);
                      const isRisk = !isDashboardCustomerContacted(ro) && mins != null && mins <= 60;

                      const promiseAlertKey = `PROMISE_RECOVERY::${keyForJump}`;
                      const snoozeEntry = dismissedMap[promiseAlertKey];
                      const isPromiseSnoozed = !!(
                        snoozeEntry && isSuppressed(snoozeEntry)
                      );

                      const latestNote = latestSharedNote(ro?.notes);
                                            const hasUnreadNote = hasUnreadThreadNote(ro);

                      const rowClass = isRisk
                        ? "ft-rowClickable ft-rowRisk"
                        : "ft-rowClickable";

                      return (
                        <tr
                          key={safeString(ro?.id || roNumber || tag || openedISO)}
                          className={rowClass}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            if (!keyForJump) return;
                            nav(`/dispatch?tag=${encodeURIComponent(keyForJump)}`);
                          }}
                          onKeyDown={(e) => {
                            if ((e.key === "Enter" || e.key === " ") && keyForJump) {
                              e.preventDefault();
                              nav(`/dispatch?tag=${encodeURIComponent(keyForJump)}`);
                            }
                          }}
                          title="Open in Dispatch"
                          style={
                            isPromiseSnoozed
                              ? {
                                  opacity: 0.78,
                                }
                              : undefined
                          }
                        >
                          <td style={{ paddingTop: 6, paddingBottom: 6 }}>
                            <div className="ft-roMain">
                              <div
                                className="ft-roTitle"
                                style={{ fontSize: 11.7, lineHeight: 1.04 }}
                              >
                                {roNumber ? `RO #${roNumber}` : tag ? `Tag #${tag}` : "RO"}
                              </div>
                              <div
                                className="ft-roSub"
                                style={{ fontSize: 10.65, marginTop: 1 }}
                              >
                                {[customer || null, vehicle || null]
                                  .filter(Boolean)
                                  .join(" — ") || "—"}
                              </div>
                            </div>
                          </td>

                          <td
                            style={{
                              fontSize: 10.55,
                              paddingTop: 6,
                              paddingBottom: 6,
                              whiteSpace: "nowrap",
                            }}
                            title={createdISO || ""}
                          >
                            {created}
                          </td>

                          <td style={{ fontSize: 10.75, paddingTop: 6, paddingBottom: 6 }}>
                            <div style={{ lineHeight: 1.08 }}>
                              <div style={shopAgeTextStyle(ro)}>{shopAge}</div>
                            </div>
                          </td>

                          <td style={{ paddingTop: 6, paddingBottom: 6 }}>
                            <span className={pillClassForOperationalStatus(operationalStatus.key)}>
                              {operationalStatus.label}
                            </span>
                          </td>

                          <td style={{ paddingTop: 6, paddingBottom: 6 }}>
                            <span className={pillClassForTransport(transport)}>{transport}</span>
                          </td>

                          <td style={{ paddingTop: 6, paddingBottom: 6 }}>
                            {isRisk ? (
                              <span
                                className="ft-pill"
                                style={{
                                  ...promiseSeverityStyle(mins, isPromiseSnoozed),
                                  fontWeight: 850,
                                  fontSize: 10.35,
                                  padding: "3px 8px",
                                }}
                                title={
                                  isPromiseSnoozed
                                    ? `Snoozed: ${
                                        safeString(snoozeEntry?.reason).trim() || "Tracked"
                                      } until ${formatDateTimeDisplay(
                                        snoozeEntry?.snoozeUntil,
                                        tz
                                      )}`
                                    : promiseLabel
                                }
                              >
                                {promiseLabel}
                              </span>
                            ) : (
                              <span className="ft-caption" style={{ fontSize: 10.95 }}>
                                {promiseLabel}
                              </span>
                            )}
                          </td>

                          <td style={{ paddingTop: 6, paddingBottom: 6 }}>
                            <span
                              className="ft-caption"
                              style={{ fontSize: 10.9 }}
                              title={latestNote || "No shared note yet"}
                            >
                              {latestNote || "—"}
                            </span>
                          </td>

                                                    <td style={{ textAlign: "right", paddingTop: 6, paddingBottom: 6 }}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openThreadForRo(ro);
                              }}
                              onKeyDown={(e) => e.stopPropagation()}
                              style={managerThreadButtonStyle(hasUnreadNote)}
                              title={hasUnreadNote ? "Unread note activity" : "Open notes and activity"}
                            >
                              Notes / Activity
                              {hasUnreadNote ? <ManagerUnreadDot /> : null}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {activeSnoozes.length === 0 ? (
            <div
              className="ft-card"
              style={{
                padding: "8px 12px",
                borderRadius: 14,
                border: "1px solid rgba(15,23,42,0.05)",
                background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={denseSectionTitleStyle()}>Current Snoozes</div>
                  <div className="ft-caption" style={{ marginTop: 2, fontSize: 11 }}>
                    No active snoozes
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="ft-card"
              style={{
                ...dashboardShellCardStyle(),
                paddingTop: 10,
                paddingBottom: 9,
                border: "1px solid rgba(15,23,42,0.05)",
                background:
                  "linear-gradient(180deg, rgba(249,250,251,0.74) 0%, rgba(255,255,255,1) 100%)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  marginBottom: 6,
                }}
              >
                <div>
                  <div style={denseSectionTitleStyle()}>Current Snoozes</div>
                  <div className="ft-caption" style={{ marginTop: 4, fontSize: 11.2 }}>
                    Items temporarily hidden from Needs Action.
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                {activeSnoozes.map((item) => (
                  <CurrentSnoozeRow
                    key={item.alertKey}
                    item={item}
                    tz={tz}
                    onRestore={() => restoreSnooze(item.alertKey)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "grid", gap: 9, minWidth: 0 }}>
          <div
            className="ft-card"
            style={{
              ...dashboardShellCardStyle(),
              padding: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                alignItems: "flex-start",
                flexWrap: "wrap",
                marginBottom: 8,
              }}
            >
              <div>
                <div style={denseSectionTitleStyle()}>Today</div>
                <div className="ft-caption" style={{ marginTop: 4, fontSize: 10.95 }}>
                  Store date: <b>{todayKey}</b> • Updated {formatTimeShort(lastUpdatedISO)}
                </div>
              </div>

              <span
                className="ft-pill"
                style={{
                  fontSize: 10.4,
                  fontWeight: 900,
                  border:
                    snapshotStatus.tone === "good"
                      ? "1px solid rgba(6,118,71,0.14)"
                      : snapshotStatus.tone === "warn"
                      ? "1px solid rgba(181,71,8,0.14)"
                      : "1px solid rgba(15,23,42,0.10)",
                  background:
                    snapshotStatus.tone === "good"
                      ? "rgba(236,253,243,0.84)"
                      : snapshotStatus.tone === "warn"
                      ? "rgba(255,247,237,0.84)"
                      : "rgba(248,250,252,0.94)",
                  color:
                    snapshotStatus.tone === "good"
                      ? "#067647"
                      : snapshotStatus.tone === "warn"
                      ? "#b54708"
                      : "rgba(15,23,42,0.64)",
                }}
              >
                {snapshotStatus.label}
              </span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 6,
              }}
            >
              <MetricCard
                label="Open WIP"
                value={operational.openWip}
                meta={kpiTone(operational.openWip, "openWip")}
                compact
              />
              <MetricCard
                label="Promise Risk"
                value={operational.promiseRisk}
                meta={kpiTone(operational.promiseRisk, "promiseRisk")}
                compact
              />
              <MetricCard
                label="Unassigned"
                value={operational.unassigned}
                meta={kpiTone(operational.unassigned, "unassigned")}
                compact
              />
              <MetricCard
                label="Waiting Parts"
                value={operational.waitingOnParts}
                meta={kpiTone(operational.waitingOnParts, "waitingParts")}
                compact
              />
              <MetricCard
                label="In Progress"
                value={operational.inProgress}
                meta={kpiTone(operational.inProgress, "inProgress")}
                compact
              />
              <MetricCard
                label="Completed Today"
                value={operational.completedToday}
                meta={kpiTone(operational.completedToday, "completedToday")}
                compact
              />
            </div>
          </div>

          <div
            className="ft-card"
            style={{
              ...dashboardShellCardStyle(),
              padding: 10,
              paddingBottom: 9,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                alignItems: "flex-start",
                marginBottom: 8,
              }}
            >
              <div>
                <div style={denseSectionTitleStyle()}>Historical WIP Trend</div>
                <div className="ft-caption" style={{ marginTop: 4, fontSize: 10.95 }}>
                  Last 14 store-days • midnight snapshots
                </div>
              </div>

              <div
                className="ft-pill"
                style={{
                  fontSize: 10.4,
                  fontWeight: 900,
                  border:
                    snapshotStatus.tone === "good"
                      ? "1px solid rgba(6,118,71,0.14)"
                      : snapshotStatus.tone === "warn"
                      ? "1px solid rgba(181,71,8,0.14)"
                      : "1px solid rgba(15,23,42,0.10)",
                  background:
                    snapshotStatus.tone === "good"
                      ? "rgba(236,253,243,0.84)"
                      : snapshotStatus.tone === "warn"
                      ? "rgba(255,247,237,0.84)"
                      : "rgba(248,250,252,0.94)",
                  color:
                    snapshotStatus.tone === "good"
                      ? "#067647"
                      : snapshotStatus.tone === "warn"
                      ? "#b54708"
                      : "rgba(15,23,42,0.64)",
                }}
              >
                {snapshotStatus.label}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 6,
                marginBottom: 8,
              }}
            >
              <MiniSnapshotMetric
                label="Latest WIP"
                value={safeNum(latestSnapshot?.wipCount ?? latestSnapshot?.openWip ?? 0)}
                muted={!latestSnapshot}
              />
              <MiniSnapshotMetric
                label="Waiters"
                value={safeNum(latestSnapshot?.waiterCount ?? 0)}
                muted={!latestSnapshot}
              />
              <MiniSnapshotMetric
                label="Unassigned"
                value={safeNum(latestSnapshot?.dispatchCount ?? 0)}
                muted={!latestSnapshot}
              />
            </div>

            <div className="ft-trendList" style={{ padding: 0, maxHeight: 132 }}>
              {trendDays.map((d, idx) => {
                const isToday = idx === trendDays.length - 1;
                const normalized = d.has
                  ? Math.round(((safeNum(d.wipCount) - trendStats.min) / trendStats.range) * 72) + 24
                  : 0;
                const width = `${Math.max(6, Math.min(100, normalized))}%`;

                return (
                  <div
                    key={d.dateKey}
                    className="ft-trendRow"
                    style={{
                      padding: "3px 0",
                      opacity: d.has ? (idx < 4 ? 0.72 : 1) : 0.32,
                    }}
                    title={
                      d.has
                        ? `${d.dateKey}: ${d.wipCount} WIP, ${d.waiterCount} waiters, ${d.dispatchCount} unassigned`
                        : `${d.dateKey}: no snapshot captured yet`
                    }
                  >
                    <div
                      className="ft-trendDate"
                      style={{
                        fontWeight: isToday ? 800 : 600,
                        fontSize: 10.9,
                        color: isToday ? "rgba(15,23,42,0.86)" : undefined,
                      }}
                    >
                      {d.dateKey.slice(5)}
                    </div>

                    <div className="ft-trendBar" style={{ height: 7 }}>
                      <div
                        className="ft-trendFill"
                        style={{
                          width,
                          background: isToday
                            ? "linear-gradient(90deg, rgba(37,99,235,0.46), rgba(37,99,235,0.78))"
                            : d.wipCount >= trendStats.max
                            ? "linear-gradient(90deg, rgba(180,35,24,0.24), rgba(180,35,24,0.48))"
                            : undefined,
                        }}
                      />
                    </div>

                    <div
                      className="ft-trendValue"
                      style={{ fontWeight: isToday ? 800 : 600, fontSize: 10.9 }}
                    >
                      {d.has ? d.wipCount : "—"}
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              className="ft-caption"
              style={{
                marginTop: 7,
                fontSize: 10.55,
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span>{snapshotStatus.text}</span>
              <span>
                Coverage: <b>{snapshotCoverage}/14</b> days • Last midnight capture: <b>{latestSnapshot?.dateKey || "—"}</b> • Δ yesterday: <b>{trendDelta > 0 ? `+${trendDelta}` : trendDelta}</b>
              </span>
            </div>
          </div>

          <div
            className="ft-card"
            style={{
              ...dashboardShellCardStyle(),
              padding: 10,
              paddingBottom: 9,
              border: "1px solid rgba(181,71,8,0.07)",
              background:
                "linear-gradient(180deg, rgba(255,247,237,0.34) 0%, rgba(255,255,255,1) 100%)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                alignItems: "flex-start",
                flexWrap: "wrap",
                marginBottom: patternAlerts.length ? 5 : 4,
              }}
            >
              <div>
                <div style={denseSectionTitleStyle()}>Trend Alerts</div>
                <div className="ft-caption" style={{ marginTop: 4, fontSize: 10.9 }}>
                  Repeating patterns across active repair orders.
                </div>
              </div>
            </div>

            {patternAlerts.length === 0 ? (
              <div className="ft-caption" style={{ padding: "5px 2px 0", fontSize: 10.9 }}>
                No repeating pressure patterns detected right now.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 5 }}>
                {patternAlerts.map((alert) => (
                  <PatternAlertRow
                    key={alert.key}
                    title={alert.title}
                    subtitle={alert.subtitle}
                    subtle
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {ackModalOpen ? (
        <div className="ft-modalOverlay" role="dialog" aria-modal="true">
          <div className="ft-modal" style={{ maxWidth: 620, padding: 0 }}>
            <div className="ft-modalHead" style={{ padding: 12 }}>
              <div className="ft-modalTitle" style={{ fontSize: "1rem" }}>
                Snooze warning
              </div>
              <button
                className="ft-btn ft-btn--ghost"
                onClick={closeAcknowledgeModal}
                disabled={snoozeSaving}
                style={{ padding: "8px 10px", fontSize: 12 }}
              >
                Close
              </button>
            </div>

            <div className="ft-modalBody" style={{ padding: 12, gap: 12 }}>
              <div className="ft-caption" style={{ fontSize: 11.5 }}>
                Snooze <b>{ackTarget?.label || "warning"}</b> for {ackTarget?.ro ? roLabel(ackTarget.ro) : "this RO"}.
                This does not change the repair order; it only hides this manager warning temporarily.
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                <label
                  className="ft-caption"
                  style={{ marginTop: 0, fontWeight: 800, fontSize: 11.4 }}
                >
                  Reason
                </label>

                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {ACK_REASONS.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      className={
                        ackReason === reason
                          ? "ft-chip ft-chip-active"
                          : "ft-chip"
                      }
                      onClick={() => setAckReason(reason)}
                      style={{ fontSize: 11.3, padding: "7px 10px" }}
                    >
                      {reason}
                    </button>
                  ))}
                </div>

                {ackReason === "Other" ? (
                  <input
                    value={ackOtherText}
                    onChange={(e) => setAckOtherText(e.target.value)}
                    placeholder="Enter reason"
                    style={{
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(15,23,42,0.10)",
                      background: "#fff",
                      fontSize: 12,
                      outline: "none",
                      fontFamily: "inherit",
                    }}
                  />
                ) : null}
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                <label
                  className="ft-caption"
                  style={{ marginTop: 0, fontWeight: 800, fontSize: 11.4 }}
                >
                  Snooze until
                </label>

                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {SNOOZE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={
                        snoozeMode === option.value
                          ? "ft-chip ft-chip-active"
                          : "ft-chip"
                      }
                      onClick={() => setSnoozeMode(option.value)}
                      style={{ fontSize: 11.3, padding: "7px 10px" }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {snoozeMode === "CUSTOM" ? (
                  <input
                    type="datetime-local"
                    value={customSnoozeInput}
                    onChange={(e) => setCustomSnoozeInput(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(15,23,42,0.10)",
                      background: "#fff",
                      fontSize: 12,
                      outline: "none",
                    }}
                  />
                ) : null}

                <div className="ft-caption" style={{ fontSize: 11.2 }}>
                  Hidden{" "}
                  <b>
                    {describeSnoozeMode(
                      snoozeMode,
                      parseLocalDateTimeInput(customSnoozeInput)
                    )}
                  </b>
                  . The warning will return later only if the condition is still true.
                </div>
              </div>
            </div>

            <div className="ft-modalActions" style={{ padding: 12 }}>
              <button
                className="ft-btn ft-btn--ghost"
                onClick={closeAcknowledgeModal}
                style={{ padding: "8px 10px", fontSize: 12 }}
              >
                Cancel
              </button>
              <button
                className="ft-btn"
                onClick={saveAcknowledgement}
                disabled={snoozeSaving}
                style={{ padding: "8px 10px", fontSize: 12 }}
              >
                {snoozeSaving ? "Saving…" : "Save snooze"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {healthExplainerOpen ? (
        <ShopHealthExplainer
          insights={executiveInsights}
          onClose={() => setHealthExplainerOpen(false)}
        />
      ) : null}

      <ROActivityDrawer
        ro={threadRO}
        isOpen={!!threadRO}
        onClose={closeThread}
        currentUser={currentUser}
        actorRole={actorRole}
        actorName={actorName}
        title="Manager RO Thread"
      />

      {exportOpen ? (
        <div className="ft-modalOverlay" role="dialog" aria-modal="true">
          <div className="ft-modal" style={{ maxWidth: 620, padding: 0 }}>
            <div className="ft-modalHead" style={{ padding: 12 }}>
              <div className="ft-modalTitle" style={{ fontSize: "1rem" }}>
                Export reports
              </div>
              <button
                className="ft-btn ft-btn--ghost"
                onClick={() => setExportOpen(false)}
                style={{ padding: "8px 10px", fontSize: 12 }}
              >
                Close
              </button>
            </div>

            <div className="ft-modalBody" style={{ padding: 12, gap: 10 }}>
              <label className="ft-checkRow" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  checked={exportKpiChecked}
                  onChange={(e) => setExportKpiChecked(e.target.checked)}
                />
                <div>
                  <div className="ft-checkTitle">KPI range report</div>
                  <div className="ft-checkSub">
                    Uses created/opened date range: <b>{kpiRange.from}</b> → <b>{kpiRange.to}</b>
                  </div>
                </div>
              </label>

              {exportKpiChecked ? (
                <div className="ft-miniRow">
                  <button
                    className={kpiPreset === "TODAY" ? "ft-chip ft-chip-active" : "ft-chip"}
                    onClick={() => setKpiPreset("TODAY")}
                  >
                    Today
                  </button>
                  <button
                    className={kpiPreset === "PAST_7" ? "ft-chip ft-chip-active" : "ft-chip"}
                    onClick={() => setKpiPreset("PAST_7")}
                  >
                    Past 7
                  </button>
                  <button
                    className={kpiPreset === "THIS_MONTH" ? "ft-chip ft-chip-active" : "ft-chip"}
                    onClick={() => setKpiPreset("THIS_MONTH")}
                  >
                    Month
                  </button>
                  <button
                    className={kpiPreset === "CUSTOM" ? "ft-chip ft-chip-active" : "ft-chip"}
                    onClick={() => setKpiPreset("CUSTOM")}
                  >
                    Custom
                  </button>

                  {kpiPreset === "CUSTOM" ? (
                    <>
                      <input
                        className="ft-date"
                        type="date"
                        value={customFrom}
                        onChange={(e) => setCustomFrom(e.target.value)}
                      />
                      <input
                        className="ft-date"
                        type="date"
                        value={customTo}
                        onChange={(e) => setCustomTo(e.target.value)}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}

              <label className="ft-checkRow" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  checked={exportOpenWipChecked}
                  onChange={(e) => setExportOpenWipChecked(e.target.checked)}
                />
                <div>
                  <div className="ft-checkTitle">Current open WIP report</div>
                  <div className="ft-checkSub">
                    Exports the current open WIP list for <b>{todayKey}</b>.
                  </div>
                </div>
              </label>
            </div>

            <div className="ft-modalActions" style={{ padding: 12 }}>
              <button
                className="ft-btn ft-btn--ghost"
                onClick={() => setExportOpen(false)}
                style={{ padding: "8px 10px", fontSize: 12 }}
              >
                Cancel
              </button>
              <button
                className="ft-btn"
                onClick={exportSelected}
                style={{ padding: "8px 10px", fontSize: 12 }}
              >
                Export selected
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AttentionBar({ state, items, updatedAt }) {
  const variant = state?.variant || "good";

  const style =
    variant === "danger"
      ? {
          border: "1px solid rgba(180,35,24,0.16)",
          background:
            "linear-gradient(180deg, rgba(254,243,242,0.84) 0%, rgba(255,255,255,1) 100%)",
        }
      : variant === "warn"
      ? {
          border: "1px solid rgba(181,71,8,0.14)",
          background:
            "linear-gradient(180deg, rgba(255,247,237,0.82) 0%, rgba(255,255,255,1) 100%)",
        }
      : {
          border: "1px solid rgba(6,118,71,0.14)",
          background:
            "linear-gradient(180deg, rgba(236,253,243,0.70) 0%, rgba(255,255,255,1) 100%)",
        };

  const titleColor =
    variant === "danger" ? "#b42318" : variant === "warn" ? "#b54708" : "#067647";

  return (
    <div
      className="ft-card"
      style={{
        ...style,
        borderRadius: 16,
        padding: "10px 12px",
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "flex-start",
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: titleColor,
          }}
        >
          {state?.title || "Shop status"}
        </div>
        <div className="ft-caption" style={{ marginTop: 5, fontSize: 11.5 }}>
          {state?.subtitle || "Current operational status."}
        </div>
        {items?.length ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
            {items.map((item, idx) => (
              <span
                key={`${item.text}-${idx}`}
                className="ft-pill"
                style={{
                  fontSize: 10.7,
                  fontWeight: 800,
                  border: item.danger
                    ? "1px solid rgba(180,35,24,0.16)"
                    : "1px solid rgba(15,23,42,0.08)",
                  color: item.danger ? "#b42318" : "rgba(15,23,42,0.70)",
                  background: item.danger ? "rgba(254,243,242,0.92)" : "#fff",
                }}
              >
                {item.text}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="ft-caption" style={{ marginTop: 0, fontSize: 11.2 }}>
        Updated <b>{formatTimeShort(updatedAt)}</b>
      </div>
    </div>
  );
}

function ExecutiveInsightsPanel({ insights, onExplain }) {
  return (
    <div
      className="ft-card ft-managerExecutiveGrid"
      style={{
        padding: 12,
        borderRadius: 18,
      }}
    >
      <div
        style={{
          border: "1px solid rgba(15,23,42,0.08)",
          borderRadius: 14,
          padding: 12,
          background:
            insights.health.tone === "danger"
              ? "linear-gradient(180deg, rgba(254,243,242,0.70), #fff)"
              : insights.health.tone === "warn"
              ? "linear-gradient(180deg, rgba(255,247,237,0.70), #fff)"
              : "linear-gradient(180deg, rgba(236,253,243,0.70), #fff)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            alignItems: "flex-start",
          }}
        >
          <div style={denseSectionTitleStyle()}>Shop Health</div>
          <button
            className="ft-chip"
            onClick={onExplain}
            style={{
              height: 24,
              padding: "0 8px",
              fontSize: 10.6,
              borderColor: "rgba(180,35,24,0.16)",
              color: "#b42318",
              background: "#fff",
            }}
          >
            Explain Score
          </button>
        </div>

        <div
          style={{
            marginTop: 12,
            fontSize: 34,
            lineHeight: 0.9,
            fontWeight: 900,
            letterSpacing: "-0.06em",
            color:
              insights.health.tone === "danger"
                ? "#b42318"
                : insights.health.tone === "warn"
                ? "#b54708"
                : "#067647",
          }}
        >
          {insights.score}
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            fontWeight: 900,
            color:
              insights.health.tone === "danger"
                ? "#b42318"
                : insights.health.tone === "warn"
                ? "#b54708"
                : "#067647",
          }}
        >
          {insights.health.label}
        </div>
        {insights.primaryBottleneck ? (
          <div
            style={{
              marginTop: 8,
              fontSize: 11.2,
              lineHeight: 1.35,
              color: "rgba(15,23,42,0.68)",
              fontWeight: 800,
            }}
          >
            Primary focus: {insights.primaryBottleneck}
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        {insights.cards.map((card) => (
          <div
            key={card.key}
            style={{
              ...executiveToneStyle(card.tone),
              borderRadius: 14,
              padding: 10,
              minWidth: 0,
            }}
          >
            <div
              style={{
                fontSize: 10.4,
                fontWeight: 900,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
              }}
            >
              {card.title}
            </div>
            <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "baseline" }}>
              <span style={{ fontSize: 22, lineHeight: 1, fontWeight: 900 }}>
                {card.value}
              </span>
              <span style={{ fontSize: 10.8, fontWeight: 800 }}>{card.unit}</span>
            </div>
            <div className="ft-caption" style={{ marginTop: 7, fontSize: 11.1 }}>
              {card.body}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          border: "1px solid rgba(15,23,42,0.08)",
          borderRadius: 14,
          padding: 12,
          background: "linear-gradient(180deg, #ffffff, #fbfcfd)",
        }}
      >
        <div style={denseSectionTitleStyle()}>Bottleneck Diagnosis</div>
        <div style={{ display: "grid", gap: 9, marginTop: 12 }}>
          {insights.bottlenecks.length ? (
            insights.bottlenecks.map((b, idx) => (
              <div
                key={b.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 11.6,
                    fontWeight: 900,
                    color: "rgba(15,23,42,0.84)",
                  }}
                >
                  {idx + 1}. {b.label}
                </div>
                <span
                  className="ft-pill"
                  style={{
                    fontSize: 10.7,
                    fontWeight: 900,
                    color:
                      b.tone === "danger"
                        ? "#b42318"
                        : b.tone === "warn"
                        ? "#b54708"
                        : "rgba(15,23,42,0.64)",
                    background:
                      b.tone === "danger"
                        ? "rgba(254,243,242,0.9)"
                        : b.tone === "warn"
                        ? "rgba(255,247,237,0.9)"
                        : "#fff",
                  }}
                >
                  {b.count}
                </span>
              </div>
            ))
          ) : (
            <div className="ft-caption" style={{ fontSize: 11.2 }}>
              No major bottleneck detected.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DailyShopSummary({ summary }) {
  return (
    <div
      className="ft-card"
      style={{
        padding: 12,
        borderRadius: 16,
        border: "1px solid rgba(15,23,42,0.08)",
        background: "linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={denseSectionTitleStyle()}>{summary.title}</div>
        <span
          className="ft-pill"
          style={{
            fontSize: 10.5,
            fontWeight: 900,
            border: "1px solid rgba(180,35,24,0.12)",
            background: "rgba(254,243,242,0.76)",
            color: "#b42318",
          }}
        >
          {summary.urgency}
        </span>
      </div>
      <div
        style={{
          marginTop: 10,
          fontSize: 12.2,
          lineHeight: 1.45,
          color: "rgba(15,23,42,0.86)",
          fontWeight: 650,
        }}
      >
        {summary.narrative}
      </div>
      <div
        style={{
          marginTop: 10,
          padding: "9px 10px",
          borderRadius: 12,
          border: "1px solid rgba(15,23,42,0.07)",
          background: "#fff",
          fontSize: 12,
          lineHeight: 1.35,
          fontWeight: 800,
        }}
      >
        Recommended action: {summary.recommendedAction}
      </div>
    </div>
  );
}

function MiniSnapshotMetric({ label, value, muted = false }) {
  return (
    <div
      style={{
        border: "1px solid rgba(15,23,42,0.06)",
        borderRadius: 10,
        padding: "7px 9px",
        background: muted ? "rgba(248,250,252,0.72)" : "#ffffff",
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 9.8,
          fontWeight: 900,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "rgba(15,23,42,0.50)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 3,
          fontSize: 18,
          lineHeight: 1,
          fontWeight: 900,
          letterSpacing: "-0.03em",
          color: muted ? "rgba(15,23,42,0.34)" : "rgba(15,23,42,0.88)",
        }}
      >
        {muted ? "—" : value}
      </div>
    </div>
  );
}

function MetricCard({ label, value, meta, compact = false }) {
  const tone = meta?.tone || "neutral";
  return (
    <div
      style={{
        border: "1px solid rgba(15,23,42,0.06)",
        borderRadius: compact ? 12 : 14,
        padding: compact ? "9px 10px" : 12,
        background: "#fff",
      }}
    >
      <div
        style={{
          fontSize: compact ? 9.7 : 10.5,
          fontWeight: 900,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "rgba(15,23,42,0.50)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 5,
          fontSize: compact ? 20 : 24,
          lineHeight: 1,
          fontWeight: 900,
          letterSpacing: "-0.04em",
          color: "rgba(15,23,42,0.88)",
        }}
      >
        {value}
      </div>
      {meta?.label ? (
        <div
          className="ft-caption"
          style={{
            marginTop: 5,
            fontSize: compact ? 10.3 : 11,
            ...toneTextStyle(tone),
            fontWeight: 750,
          }}
        >
          {meta.label}
        </div>
      ) : null}
    </div>
  );
}

function ActionQueueRow({
  label,
  owner,
  actionHint,
  ro,
  tz,
  rightText,
  danger,
  category,
  onJump,
  onAcknowledge,
  onOpenThread,
  hasUnreadNote = false,
}) {
  const customer = safeString(ro?.customerName).trim();
  const vehicle = safeString(ro?.vehicle).trim();
  const title = roLabel(ro);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gap: 10,
        alignItems: "center",
        padding: "7px 9px",
        borderRadius: 13,
        border: danger
          ? "1px solid rgba(180,35,24,0.15)"
          : "1px solid rgba(15,23,42,0.07)",
        borderLeft: danger
          ? "4px solid rgba(180,35,24,0.74)"
          : "4px solid rgba(37,99,235,0.22)",
        background: danger
          ? "linear-gradient(180deg, rgba(254,243,242,0.56) 0%, rgba(255,255,255,1) 100%)"
          : "#fff",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span
            style={{
              fontSize: 11.45,
              fontWeight: 900,
              color: danger ? "#b42318" : "rgba(15,23,42,0.86)",
            }}
          >
            {label}
          </span>
          <span
            className="ft-pill"
            style={{
              fontSize: 10.3,
              fontWeight: 900,
              color: "rgba(15,23,42,0.70)",
              background: "rgba(248,250,252,0.94)",
            }}
          >
            ATTN: {owner}
          </span>
        </div>
        <div className="ft-caption" style={{ fontSize: 10.9, marginTop: 3 }}>
          <b>{title}</b>
          {customer || vehicle ? ` • ${[customer, vehicle].filter(Boolean).join(" — ")}` : ""}
        </div>
        <div className="ft-caption" style={{ fontSize: 10.65, marginTop: 2 }}>
          {actionHint} {rightText ? `• ${rightText}` : ""}
        </div>
      </div>

            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => onOpenThread?.(ro)}
          style={managerThreadButtonStyle(hasUnreadNote)}
          title={hasUnreadNote ? "Unread note activity" : "Open notes and activity"}
        >
          Notes
          {hasUnreadNote ? <ManagerUnreadDot /> : null}
        </button>

        <button
          className="ft-btn"
          onClick={() => onJump?.(ro, category)}
          style={{ padding: "7px 9px", fontSize: 11.4, height: 32 }}
        >
          Open
        </button>
        <button
          className="ft-btn ft-btn--ghost"
          onClick={onAcknowledge}
          style={{ padding: "7px 9px", fontSize: 11.4, height: 32 }}
        >
          Snooze
        </button>
      </div>
    </div>
  );
}

function PatternAlertRow({ title, subtitle, subtle = false }) {
  return (
    <div
      style={{
        border: "1px solid rgba(15,23,42,0.06)",
        borderRadius: 12,
        padding: subtle ? "7px 8px" : "9px 10px",
        background: "#fff",
      }}
    >
      <div
        style={{
          fontSize: subtle ? 11.2 : 12,
          fontWeight: 850,
          color: "rgba(15,23,42,0.84)",
        }}
      >
        {title}
      </div>
      <div className="ft-caption" style={{ fontSize: subtle ? 10.7 : 11.2 }}>
        {subtitle}
      </div>
    </div>
  );
}

function ShopHealthExplainer({ insights, onClose }) {
  return (
    <div className="ft-modalOverlay" role="dialog" aria-modal="true">
      <div className="ft-modal" style={{ maxWidth: 720, padding: 0 }}>
        <div className="ft-modalHead" style={{ padding: 12 }}>
          <div className="ft-modalTitle" style={{ fontSize: "1rem" }}>
            Why this shop health score?
          </div>
          <button
            className="ft-btn ft-btn--ghost"
            onClick={onClose}
            style={{ padding: "8px 10px", fontSize: 12 }}
          >
            Close
          </button>
        </div>

        <div className="ft-modalBody" style={{ padding: 12, gap: 10 }}>
          <div
            style={{
              border: "1px solid rgba(15,23,42,0.08)",
              borderRadius: 14,
              padding: 12,
              background: "linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%)",
            }}
          >
            <div style={denseSectionTitleStyle()}>Score</div>
            <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "baseline" }}>
              <span style={{ fontSize: 34, fontWeight: 900, letterSpacing: "-0.05em" }}>
                {insights.score}
              </span>
              <span style={{ fontWeight: 900, color: toneTextStyle(insights.health.tone).color }}>
                {insights.health.label}
              </span>
            </div>
            <div className="ft-caption" style={{ fontSize: 11.5 }}>
              Flowtrak starts at 100 and subtracts for operational pressure points. This is an operational control score, not an accounting score.
            </div>
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            {insights.scoreDrivers.map((driver) => (
              <div
                key={driver.label}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gap: 10,
                  alignItems: "center",
                  border: "1px solid rgba(15,23,42,0.08)",
                  borderRadius: 13,
                  padding: "9px 10px",
                  background: "#fff",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: "rgba(15,23,42,0.86)",
                    }}
                  >
                    {driver.label}: {driver.value}
                  </div>
                  <div className="ft-caption" style={{ fontSize: 11.1 }}>
                    {driver.note}
                  </div>
                  <div className="ft-caption" style={{ fontSize: 11.1, fontWeight: 750 }}>
                    Action: {driver.action}
                  </div>
                </div>
                <span
                  className="ft-pill"
                  style={{
                    minWidth: 42,
                    justifyContent: "center",
                    fontWeight: 900,
                    color: toneTextStyle(driver.tone).color,
                  }}
                >
                  {driver.impact}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CurrentSnoozeRow({ item, tz, onRestore }) {
  const actorName = safeString(item?.actedByName).trim();
  const actorRole = safeString(item?.actedByRole).trim();
  const actorLabel = actorName
    ? `${actorName}${actorRole ? ` (${prettyAlertType(actorRole)})` : ""}`
    : "Store team";

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gap: 8,
        alignItems: "center",
        border: "1px solid rgba(15,23,42,0.06)",
        borderRadius: 12,
        padding: "7px 8px",
        background: "#fff",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 11.2,
            fontWeight: 850,
            color: "rgba(15,23,42,0.84)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {prettyAlertType(item.category)} • {item.roNumber ? `RO #${item.roNumber}` : item.tagNumber ? `Tag #${item.tagNumber}` : "RO"}
        </div>
        <div className="ft-caption" style={{ fontSize: 10.7 }}>
          {safeString(item.reason).trim() || "Tracked"} • {actorLabel} • returns {formatDateTimeDisplay(item.snoozeUntil, tz)}
        </div>
      </div>
      <button
        className="ft-btn ft-btn--ghost"
        onClick={onRestore}
        style={{ height: 30, padding: "6px 8px", fontSize: 11 }}
      >
        Restore
      </button>
    </div>
  );
}
